import type { Graphics } from 'pixi.js';
import type { BuildingType } from '../sim/types';

// All drawing happens in "V-space": the footprint's north grid vertex is the
// origin. Ground corners come from the iso transform; shapes may extend
// anywhere (false fronts above, shadows below) — the caller crops to bounds.
//
// Light: sun from the west-top. Top faces lightest, SW walls mid, SE darkest.

export interface BuildingMeta {
  attachX: number;
  attachY: number;
  anim: { kind: 'exhaust' | 'sign' | 'drip'; x: number; y: number } | null;
}

interface Corners {
  ex: number;
  ey: number;
  sx: number;
  sy: number;
  wx: number;
  wy: number;
}

function corners(w: number, h: number): Corners {
  return { ex: w * 32, ey: w * 16, sx: (w - h) * 32, sy: (w + h) * 16, wx: -h * 32, wy: h * 16 };
}

function darken(color: number, factor: number): number {
  const r = Math.min(255, Math.round(((color >> 16) & 0xff) * factor));
  const g = Math.min(255, Math.round(((color >> 8) & 0xff) * factor));
  const b = Math.min(255, Math.round((color & 0xff) * factor));
  return (r << 16) | (g << 8) | b;
}

const OUTLINE = { width: 1, color: 0x261b12, alpha: 0.9 };

/** A vertical wall whose bottom edge runs (x0,y0)->(x1,y1), extruded up h px. */
interface Wall {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  h: number;
}

function swWall(c: Corners, h: number): Wall {
  return { x0: c.wx, y0: c.wy, x1: c.sx, y1: c.sy, h };
}

function seWall(c: Corners, h: number): Wall {
  return { x0: c.sx, y0: c.sy, x1: c.ex, y1: c.ey, h };
}

function wallPoint(wl: Wall, t: number, up: number): { x: number; y: number } {
  return { x: wl.x0 + (wl.x1 - wl.x0) * t, y: wl.y0 + (wl.y1 - wl.y0) * t - up };
}

function wallSlope(wl: Wall): number {
  return (wl.y1 - wl.y0) / (wl.x1 - wl.x0);
}

function fillWall(g: Graphics, wl: Wall, color: number): void {
  g.poly([wl.x0, wl.y0 - wl.h, wl.x1, wl.y1 - wl.h, wl.x1, wl.y1, wl.x0, wl.y0]).fill(color);
}

/** Horizontal plank lines following the wall's skew. */
function siding(g: Graphics, wl: Wall, gap: number, alpha = 0.14): void {
  for (let up = gap; up < wl.h - 1; up += gap) {
    g.moveTo(wl.x0, wl.y0 - up).lineTo(wl.x1, wl.y1 - up).stroke({ width: 1, color: 0x000000, alpha });
  }
}

/** Vertical ridge lines: corrugated metal. */
function corrugation(g: Graphics, wl: Wall, gap = 4): void {
  const length = Math.hypot(wl.x1 - wl.x0, wl.y1 - wl.y0);
  for (let d = gap; d < length - 1; d += gap) {
    const t = d / length;
    const top = wallPoint(wl, t, wl.h - 1);
    const bottom = wallPoint(wl, t, 1);
    g.moveTo(bottom.x, bottom.y).lineTo(top.x, top.y).stroke({ width: 1, color: 0x000000, alpha: 0.12 });
  }
}

/** Stone blocks: mortar rows + staggered verticals. */
function stonework(g: Graphics, wl: Wall, rowGap = 6): void {
  const length = Math.hypot(wl.x1 - wl.x0, wl.y1 - wl.y0);
  let row = 0;
  for (let up = rowGap; up < wl.h - 1; up += rowGap) {
    g.moveTo(wl.x0, wl.y0 - up).lineTo(wl.x1, wl.y1 - up).stroke({ width: 1, color: 0x000000, alpha: 0.18 });
    const offset = row % 2 === 0 ? 0.5 : 0;
    for (let block = 0; block < length / 9; block += 1) {
      const t = ((block + offset) * 9) / length;
      if (t <= 0.02 || t >= 0.98) continue;
      const a = wallPoint(wl, t, up);
      g.moveTo(a.x, a.y).lineTo(a.x, a.y + rowGap - 1).stroke({ width: 1, color: 0x000000, alpha: 0.14 });
    }
    row += 1;
  }
}

function aoBase(g: Graphics, wl: Wall): void {
  g.poly([wl.x0, wl.y0 - 3, wl.x1, wl.y1 - 3, wl.x1, wl.y1, wl.x0, wl.y0]).fill({ color: 0x000000, alpha: 0.22 });
}

/** Rect whose top/bottom edges follow the wall skew; x centered on cx. */
function skewRect(g: Graphics, cx: number, yBottom: number, w: number, h: number, slope: number): number[] {
  const halfW = w / 2;
  const points = [
    cx - halfW, yBottom - slope * halfW - h,
    cx + halfW, yBottom + slope * halfW - h,
    cx + halfW, yBottom + slope * halfW,
    cx - halfW, yBottom - slope * halfW,
  ];
  return points;
}

interface WindowOpts {
  lit?: boolean;
  bars?: boolean;
  shaded?: boolean;
  wpx?: number;
  hpx?: number;
}

function windowOn(g: Graphics, wl: Wall, t: number, upCenter: number, opts: WindowOpts = {}): void {
  const wpx = opts.wpx ?? 8;
  const hpx = opts.hpx ?? 10;
  const slope = wallSlope(wl);
  const center = wallPoint(wl, t, upCenter);
  const yBottom = center.y + hpx / 2;
  g.poly(skewRect(g, center.x, yBottom + 1, wpx + 4, 2, slope)).fill({ color: 0xffffff, alpha: 0.18 }); // sill
  g.poly(skewRect(g, center.x, yBottom, wpx + 2, hpx + 2, slope)).fill(0x4a3626); // frame
  const glass = opts.lit ? 0xf0c96a : opts.shaded ? 0x232c36 : 0x36414d;
  g.poly(skewRect(g, center.x, yBottom - 1, wpx, hpx, slope)).fill(glass);
  if (opts.lit) {
    g.poly(skewRect(g, center.x - wpx / 4, yBottom - 1, wpx / 3, hpx, slope)).fill({ color: 0xfff2c0, alpha: 0.5 });
  }
  // Mullion cross.
  g.moveTo(center.x, yBottom - 1).lineTo(center.x, yBottom - 1 - hpx).stroke({ width: 1, color: 0x4a3626 });
  const midY = yBottom - hpx / 2;
  g.moveTo(center.x - wpx / 2, midY - slope * (wpx / 2)).lineTo(center.x + wpx / 2, midY + slope * (wpx / 2)).stroke({ width: 1, color: 0x4a3626 });
  if (opts.bars) {
    for (const off of [-2, 2]) {
      g.moveTo(center.x + off, yBottom - 1).lineTo(center.x + off, yBottom - 1 - hpx).stroke({ width: 1, color: 0x8a8f98 });
    }
  }
}

function doorOn(g: Graphics, wl: Wall, t: number, wpx = 9, hpx = 14, color = 0x2e2118): { x: number; y: number } {
  const slope = wallSlope(wl);
  const base = wallPoint(wl, t, 0);
  g.poly(skewRect(g, base.x, base.y + 2, wpx + 5, 2, slope)).fill({ color: 0xd8c9a4, alpha: 0.5 }); // step
  g.poly(skewRect(g, base.x, base.y, wpx + 2, hpx + 2, slope)).fill(0x4a3626); // jamb
  g.poly(skewRect(g, base.x, base.y, wpx, hpx, slope)).fill(color);
  g.moveTo(base.x, base.y).lineTo(base.x, base.y - hpx).stroke({ width: 1, color: 0x000000, alpha: 0.25 });
  return base;
}

// ---- 3x5 pixel font ----------------------------------------------------

const FONT: Record<string, number[]> = {
  A: [0b010, 0b101, 0b111, 0b101, 0b101],
  B: [0b110, 0b101, 0b110, 0b101, 0b110],
  C: [0b011, 0b100, 0b100, 0b100, 0b011],
  D: [0b110, 0b101, 0b101, 0b101, 0b110],
  E: [0b111, 0b100, 0b110, 0b100, 0b111],
  F: [0b111, 0b100, 0b110, 0b100, 0b100],
  G: [0b011, 0b100, 0b101, 0b101, 0b011],
  K: [0b101, 0b110, 0b100, 0b110, 0b101],
  H: [0b101, 0b101, 0b111, 0b101, 0b101],
  I: [0b111, 0b010, 0b010, 0b010, 0b111],
  J: [0b001, 0b001, 0b001, 0b101, 0b010],
  L: [0b100, 0b100, 0b100, 0b100, 0b111],
  M: [0b111, 0b111, 0b101, 0b101, 0b101],
  N: [0b101, 0b111, 0b111, 0b101, 0b101],
  O: [0b010, 0b101, 0b101, 0b101, 0b010],
  P: [0b110, 0b101, 0b110, 0b100, 0b100],
  Q: [0b010, 0b101, 0b101, 0b010, 0b001],
  R: [0b110, 0b101, 0b110, 0b101, 0b101],
  S: [0b011, 0b100, 0b010, 0b001, 0b110],
  T: [0b111, 0b010, 0b010, 0b010, 0b010],
  U: [0b101, 0b101, 0b101, 0b101, 0b111],
  V: [0b101, 0b101, 0b101, 0b101, 0b010],
  W: [0b101, 0b101, 0b101, 0b111, 0b101],
  X: [0b101, 0b101, 0b010, 0b101, 0b101],
  Y: [0b101, 0b101, 0b010, 0b010, 0b010],
  Z: [0b111, 0b001, 0b010, 0b100, 0b111],
};

function pixelText(g: Graphics, text: string, x: number, y: number, color: number, scale = 1): number {
  let cursor = x;
  for (const ch of text) {
    const glyph = FONT[ch];
    if (glyph) {
      for (let row = 0; row < 5; row += 1) {
        for (let col = 0; col < 3; col += 1) {
          if (glyph[row]! & (0b100 >> col)) g.rect(cursor + col * scale, y + row * scale, scale, scale).fill(color);
        }
      }
    }
    cursor += 4 * scale;
  }
  return cursor - x - scale;
}

/** Centered sign board with text on a wall at height `up`. Boards are drawn
 * flat-on (not skewed) so the pixel lettering stays crisp and readable. */
function signBoard(g: Graphics, wl: Wall, t: number, up: number, text: string, board = 0xe8dcc0, ink = 0x2e2014): void {
  const width = text.length * 4 + 3;
  const center = wallPoint(wl, t, up);
  g.rect(center.x - width / 2 - 1, center.y - 5, width + 2, 10).fill(0x4a3626);
  g.rect(center.x - width / 2, center.y - 4, width, 8).fill(board);
  pixelText(g, text, center.x - (text.length * 4 - 1) / 2, center.y - 3, ink);
}

/** Filled five-point star. */
function star(g: Graphics, x: number, y: number, rOuter: number, color: number): void {
  const points: number[] = [];
  for (let index = 0; index < 10; index += 1) {
    const radius = index % 2 === 0 ? rOuter : rOuter * 0.45;
    const angle = (index / 10) * Math.PI * 2 - Math.PI / 2;
    points.push(x + Math.cos(angle) * radius, y + Math.sin(angle) * radius);
  }
  g.poly(points).fill(color);
}

/** Small rooftop box (AC unit / vent) at roof-space coordinates. */
function roofBox(g: Graphics, x: number, y: number, size: number, color: number): void {
  const half = size / 2;
  const rise = size * 0.4;
  g.poly([x, y - rise, x + half, y - rise + half / 2, x, y - rise + half, x - half, y - rise + half / 2]).fill(
    darken(color, 1.15),
  );
  g.poly([x - half, y - rise + half / 2, x, y - rise + half, x, y + half, x - half, y + half / 2]).fill(darken(color, 0.9));
  g.poly([x, y - rise + half, x + half, y - rise + half / 2, x + half, y + half / 2, x, y + half]).fill(darken(color, 0.7));
}

// ---- roofs, porches, props ----------------------------------------------

function shadowSkirt(g: Graphics, c: Corners): void {
  g.poly([c.wx - 4, c.wy + 1, c.sx, c.sy + 6, c.ex + 4, c.ey + 1, c.ex, c.ey, c.sx, c.sy, c.wx, c.wy]).fill({
    color: 0x000000,
    alpha: 0.2,
  });
}

function boxWalls(g: Graphics, c: Corners, h: number, wall: number, top: number | null = null): void {
  if (top !== null) g.poly([0, -h, c.ex, c.ey - h, c.sx, c.sy - h, c.wx, c.wy - h]).fill(top);
  fillWall(g, swWall(c, h), darken(wall, 0.95));
  fillWall(g, seWall(c, h), darken(wall, 0.72));
}

function outlineBox(g: Graphics, c: Corners, h: number): void {
  g.poly([c.wx, c.wy - h, 0, -h, c.ex, c.ey - h, c.ex, c.ey, c.sx, c.sy, c.wx, c.wy]).stroke(OUTLINE);
  g.moveTo(c.sx, c.sy).lineTo(c.sx, c.sy - h).stroke({ width: 1, color: 0xffffff, alpha: 0.18 });
}

function flatParapetRoof(g: Graphics, c: Corners, h: number, color: number): void {
  g.poly([0, -h, c.ex, c.ey - h, c.sx, c.sy - h, c.wx, c.wy - h]).fill(color);
  const cx = c.sx / 2;
  const cy = c.sy / 2 - h;
  const inset = 0.8;
  g.poly([
    cx + (0 - cx) * inset, cy + (-h - cy + h) * inset - 0,
    cx + (c.ex - cx) * inset, cy + (c.ey - h - cy) * inset,
    cx + (c.sx - cx) * inset, cy + (c.sy - h - cy) * inset,
    cx + (c.wx - cx) * inset, cy + (c.wy - h - cy) * inset,
  ]).stroke({ width: 1, color: 0x000000, alpha: 0.25 });
  // Tar seams.
  g.moveTo(cx + (c.wx - cx) * 0.5, cy + (c.wy - h - cy) * 0.5)
    .lineTo(cx + (c.ex - cx) * 0.5, cy + (c.ey - h - cy) * 0.5)
    .stroke({ width: 1, color: 0x000000, alpha: 0.12 });
}

function gableRoof(g: Graphics, c: Corners, wallH: number, rise: number, color: number): void {
  const r1x = c.wx / 2;
  const r1y = c.wy / 2 - wallH - rise;
  const r2x = (c.ex + c.sx) / 2;
  const r2y = (c.ey + c.sy) / 2 - wallH - rise;
  const north: number[] = [0, -wallH, c.ex, c.ey - wallH, r2x, r2y, r1x, r1y];
  const south: number[] = [c.wx, c.wy - wallH, c.sx, c.sy - wallH, r2x, r2y, r1x, r1y];
  g.poly(north).fill(darken(color, 1.14));
  g.poly(south).fill(darken(color, 0.78));
  // Shingle rows on both planes.
  for (const [ax, ay, bx, by] of [
    [0, -wallH, c.ex, c.ey - wallH],
    [c.wx, c.wy - wallH, c.sx, c.sy - wallH],
  ] as const) {
    for (const f of [0.3, 0.55, 0.8]) {
      g.moveTo(ax + (r1x - ax) * f, ay + (r1y - ay) * f)
        .lineTo(bx + (r2x - bx) * f, by + (r2y - by) * f)
        .stroke({ width: 1, color: 0x000000, alpha: 0.16 });
    }
  }
  // Ridge cap.
  g.moveTo(r1x, r1y).lineTo(r2x, r2y).stroke({ width: 2, color: darken(color, 0.6) });
  g.poly(north).stroke({ ...OUTLINE, alpha: 0.5 });
}

/** Raised false front above the SE wall, with cornice + optional text. */
function falseFront(g: Graphics, c: Corners, wallH: number, extra: number, color: number, text: string | null): void {
  const wl = seWall(c, wallH);
  const face = darken(color, 0.8);
  g.poly([c.sx, c.sy - wallH - extra, c.ex, c.ey - wallH - extra, c.ex, c.ey - wallH, c.sx, c.sy - wallH]).fill(face);
  siding(g, { ...wl, y0: wl.y0 - wallH, y1: wl.y1 - wallH, h: extra } as Wall, 4, 0.1);
  // Cornice with bracket ticks.
  const cornY = -wallH - extra;
  g.poly([c.sx, c.sy + cornY, c.ex, c.ey + cornY, c.ex, c.ey + cornY + 4, c.sx, c.sy + cornY + 4]).fill(0xd8c9a4);
  for (const f of [0.12, 0.36, 0.64, 0.88]) {
    const p = wallPoint(seWall(c, wallH + extra - 5), f, 0);
    g.rect(p.x - 1, p.y, 2, 3).fill({ color: 0x000000, alpha: 0.35 });
  }
  if (text) signBoard(g, seWall(c, 0), 0.5, wallH + extra - 13, text);
  g.poly([c.sx, c.sy - wallH - extra, c.ex, c.ey - wallH - extra, c.ex, c.ey - wallH, c.sx, c.sy - wallH]).stroke(OUTLINE);
}

/** Porch off the SE wall: deck, posts, shed roof, shadow. */
function porchSE(g: Graphics, c: Corners, wallH: number, depth: number, roofH: number): void {
  const wl = seWall(c, 0);
  const dirX = 0.45;
  const dirY = 0.9;
  const ox = dirX * depth;
  const oy = dirY * depth;
  // Deck.
  g.poly([wl.x0, wl.y0, wl.x1, wl.y1, wl.x1 + ox, wl.y1 + oy, wl.x0 + ox, wl.y0 + oy]).fill(0x9a7a4e);
  g.moveTo(wl.x0 + ox * 0.5, wl.y0 + oy * 0.5).lineTo(wl.x1 + ox * 0.5, wl.y1 + oy * 0.5).stroke({ width: 1, color: 0x000000, alpha: 0.2 });
  // Shed roof plane from wall down toward posts.
  const topA = { x: wl.x0, y: wl.y0 - roofH };
  const topB = { x: wl.x1, y: wl.y1 - roofH };
  const outA = { x: wl.x0 + ox, y: wl.y0 + oy - roofH + 5 };
  const outB = { x: wl.x1 + ox, y: wl.y1 + oy - roofH + 5 };
  g.poly([topA.x, topA.y, topB.x, topB.y, outB.x, outB.y, outA.x, outA.y]).fill({ color: 0x6b4f36, alpha: 0.98 });
  g.poly([topA.x, topA.y, topB.x, topB.y, outB.x, outB.y, outA.x, outA.y]).stroke({ ...OUTLINE, alpha: 0.5 });
  // Shadow band on the wall under the roof.
  g.poly([wl.x0, wl.y0 - roofH + 1, wl.x1, wl.y1 - roofH + 1, wl.x1, wl.y1 - roofH + 6, wl.x0, wl.y0 - roofH + 6]).fill({
    color: 0x000000,
    alpha: 0.15,
  });
  // Posts.
  for (const f of [0.08, 0.5, 0.92]) {
    const px = wl.x0 + (wl.x1 - wl.x0) * f + ox;
    const py = wl.y0 + (wl.y1 - wl.y0) * f + oy;
    g.rect(px - 1, py - roofH + 5, 2, roofH - 5).fill(0x6b4f36);
  }
}

function barrel(g: Graphics, x: number, y: number): void {
  g.ellipse(x, y, 3.5, 1.8).fill({ color: 0x000000, alpha: 0.2 });
  g.rect(x - 3, y - 8, 6, 8).fill(0x8a6a3a);
  g.moveTo(x - 3, y - 3).lineTo(x + 3, y - 3).stroke({ width: 1, color: 0x4a3626 });
  g.moveTo(x - 3, y - 6).lineTo(x + 3, y - 6).stroke({ width: 1, color: 0x4a3626 });
  g.ellipse(x, y - 8, 3, 1.5).fill(0xa8845a);
}

function crate(g: Graphics, x: number, y: number, size = 7): void {
  g.rect(x - size / 2, y - size, size, size).fill(0xb0895c);
  g.rect(x - size / 2, y - size, size, size).stroke({ width: 1, color: 0x4a3626, alpha: 0.7 });
  g.moveTo(x - size / 2, y - size).lineTo(x + size / 2, y).stroke({ width: 1, color: 0x4a3626, alpha: 0.5 });
}

// ---- palettes ------------------------------------------------------------

const WOOD = 0xc9a26b;
const WOOD_DARK = 0x6b4f36;
const CREAM = 0xe4d5b0;
const ROOF_BROWN = 0x7a4f34;
const STEEL = 0x99a0a8;

type Drawer = (g: Graphics, c: Corners) => BuildingMeta;

const DRAWERS: Record<BuildingType, Drawer> = {
  hq: (g, c) => {
    const H = 56;
    shadowSkirt(g, c);
    boxWalls(g, c, H, 0xa8b4c0);
    siding(g, swWall(c, H), 5, 0.08);
    siding(g, seWall(c, H), 5, 0.08);
    // Floor band.
    for (const wl of [swWall(c, H), seWall(c, H)]) {
      g.poly([wl.x0, wl.y0 - 28, wl.x1, wl.y1 - 28, wl.x1, wl.y1 - 31, wl.x0, wl.y0 - 31]).fill({ color: 0x76848f, alpha: 0.9 });
    }
    // Window grids, both floors.
    for (const wl of [swWall(c, H), seWall(c, H)]) {
      const shaded = wl === seWall(c, H);
      for (const t of [0.25, 0.5, 0.75]) {
        windowOn(g, wl, t, 40, { wpx: 7, hpx: 9, shaded: true });
        if (Math.abs(t - 0.5) > 0.01) windowOn(g, wl, t, 14, { wpx: 7, hpx: 9, shaded, lit: t === 0.25 });
      }
    }
    doorOn(g, seWall(c, 0), 0.5, 10, 15, 0x3a2f28);
    flatParapetRoof(g, c, H, 0xc4ccd4);
    roofBox(g, c.sx / 2 - 12, c.sy / 2 - H - 2, 9, 0x9aa5ae);
    roofBox(g, c.sx / 2 + 10, c.sy / 2 - H + 3, 7, 0x8a959e);
    outlineBox(g, c, H);
    signBoard(g, seWall(c, 0), 0.5, 30, 'HQ');
    // Flag.
    g.rect(-1, -H - 26, 2, 26).fill(0x5a4630);
    g.poly([1, -H - 26, 14, -H - 21, 1, -H - 16]).fill(0xd06a4e);
    aoBase(g, swWall(c, H));
    aoBase(g, seWall(c, H));
    return { attachX: 0, attachY: -H - 26, anim: null };
  },
  trailer: (g, c) => {
    const H = 20;
    shadowSkirt(g, c);
    boxWalls(g, c, H, 0xd9cdb0, 0xece2c8);
    corrugation(g, swWall(c, H), 3);
    corrugation(g, seWall(c, H), 3);
    // Rounded roof cap strip.
    g.poly([0, -H - 2, c.ex, c.ey - H - 2, c.sx, c.sy - H - 2, c.wx, c.wy - H - 2]).stroke({ width: 2, color: 0xf4ecd8 });
    windowOn(g, swWall(c, H), 0.32, 11, { wpx: 7, hpx: 7 });
    doorOn(g, seWall(c, 0), 0.62, 7, 12);
    // Wheel + hitch + propane bottle.
    const wheel = wallPoint(seWall(c, 0), 0.25, 0);
    g.circle(wheel.x, wheel.y - 1, 2.5).fill(0x2a2a2e);
    g.moveTo(c.wx, c.wy).lineTo(c.wx - 7, c.wy + 3).stroke({ width: 2, color: 0x6f6f78 });
    const tank = wallPoint(swWall(c, 0), 0.15, 0);
    g.rect(tank.x - 2, tank.y - 6, 4, 6).fill(0xd0d0c8);
    outlineBox(g, c, H);
    aoBase(g, seWall(c, H));
    return { attachX: c.sx / 2, attachY: c.sy / 2 - H - 4, anim: null };
  },
  bunkhouse: (g, c) => {
    const H = 26;
    shadowSkirt(g, c);
    boxWalls(g, c, H, WOOD);
    siding(g, swWall(c, H), 4);
    siding(g, seWall(c, H), 4);
    for (const t of [0.18, 0.42, 0.66, 0.88]) windowOn(g, swWall(c, H), t, 14, { wpx: 7, hpx: 8 });
    windowOn(g, seWall(c, H), 0.7, 15, { wpx: 7, hpx: 8, shaded: true });
    doorOn(g, seWall(c, 0), 0.3);
    gableRoof(g, c, H, 15, ROOF_BROWN);
    // Stove pipe.
    const pipe = { x: c.wx / 2 + 8, y: c.wy / 2 - H - 20 };
    g.rect(pipe.x - 1.5, pipe.y, 3, 12).fill(0x5a5f68);
    g.rect(pipe.x - 2.5, pipe.y - 2, 5, 2).fill(0x3f444c);
    aoBase(g, swWall(c, H));
    aoBase(g, seWall(c, H));
    return { attachX: (c.ex + c.sx) / 2, attachY: (c.ey + c.sy) / 2 - H - 17, anim: null };
  },
  cottage: (g, c) => {
    const H = 18;
    shadowSkirt(g, c);
    boxWalls(g, c, H, 0xd9c6a0);
    siding(g, swWall(c, H), 4);
    siding(g, seWall(c, H), 4);
    windowOn(g, swWall(c, H), 0.5, 10, { wpx: 7, hpx: 7 });
    doorOn(g, seWall(c, 0), 0.55, 7, 11);
    gableRoof(g, c, H, 10, 0x8a5a3c);
    // Little chimney + doorstep flowers.
    g.rect(c.wx / 2 + 5, c.wy / 2 - H - 16, 4, 9).fill(0x6f6f78);
    const step = wallPoint(seWall(c, 0), 0.85, 0);
    g.circle(step.x + 2, step.y + 2, 1.2).fill(0xd06a4e);
    g.circle(step.x + 5, step.y + 1, 1.2).fill(0xe8c170);
    aoBase(g, seWall(c, H));
    return { attachX: c.sx / 2, attachY: c.sy / 2 - H - 12, anim: null };
  },
  familyHouse: (g, c) => {
    const H = 30;
    shadowSkirt(g, c);
    boxWalls(g, c, H, 0xdfd4bc);
    siding(g, swWall(c, H), 4, 0.1);
    siding(g, seWall(c, H), 4, 0.1);
    // Shuttered windows up and down.
    windowOn(g, swWall(c, H), 0.3, 20, { wpx: 8, hpx: 9 });
    windowOn(g, swWall(c, H), 0.7, 20, { wpx: 8, hpx: 9 });
    windowOn(g, swWall(c, H), 0.5, 9, { wpx: 9, hpx: 9, lit: true });
    windowOn(g, seWall(c, H), 0.72, 19, { wpx: 8, hpx: 9, shaded: true });
    doorOn(g, seWall(c, 0), 0.32, 9, 14, 0x5a3c28);
    outlineBox(g, c, H);
    gableRoof(g, c, H, 14, 0x6a7a8c);
    // Brick chimney through the roofline + garden strip along the SW base.
    g.rect(c.wx / 2 + 14, c.wy / 2 - H - 20, 5, 14).fill(0x9a5a48);
    g.rect(c.wx / 2 + 13, c.wy / 2 - H - 22, 7, 3).fill(0x6e4034);
    const sw = swWall(c, 0);
    for (const f of [0.15, 0.35, 0.55, 0.75]) {
      const p = wallPoint(sw, f, 0);
      g.rect(p.x - 2, p.y + 2, 5, 2).fill(0x6a8a4a);
      g.circle(p.x, p.y + 2, 1).fill(f > 0.4 ? 0xd06a4e : 0xe8c170);
    }
    aoBase(g, seWall(c, H));
    return { attachX: (c.ex + c.sx) / 2, attachY: (c.ey + c.sy) / 2 - H - 16, anim: null };
  },
  school: (g, c) => {
    const H = 26;
    shadowSkirt(g, c);
    boxWalls(g, c, H, 0xc9704f);
    siding(g, swWall(c, H), 4, 0.1);
    siding(g, seWall(c, H), 4, 0.1);
    windowOn(g, swWall(c, H), 0.28, 15, { wpx: 9, hpx: 10 });
    windowOn(g, swWall(c, H), 0.62, 15, { wpx: 9, hpx: 10 });
    windowOn(g, seWall(c, H), 0.72, 15, { wpx: 9, hpx: 10, lit: true });
    doorOn(g, seWall(c, 0), 0.3, 10, 14, 0x4a3020);
    gableRoof(g, c, H, 13, 0x7a5a44);
    // Bell cupola on the ridge.
    const cx = (c.wx / 2 + (c.ex + c.sx) / 2) / 2;
    const cy = (c.wy / 2 + (c.ey + c.sy) / 2) / 2 - H - 13;
    g.rect(cx - 4, cy - 10, 8, 8).fill(0xe4d5b0);
    g.poly([cx - 6, cy - 10, cx, cy - 16, cx + 6, cy - 10]).fill(0x7a5a44);
    g.circle(cx, cy - 6, 1.8).fill(0x8a6a3a);
    signBoard(g, seWall(c, 0), 0.62, H - 4, 'SCHOOL');
    aoBase(g, seWall(c, H));
    return { attachX: cx, attachY: cy - 16, anim: null };
  },
  hospital: (g, c) => {
    const H = 32;
    shadowSkirt(g, c);
    boxWalls(g, c, H, 0xe8e4d8);
    siding(g, swWall(c, H), 5, 0.06);
    siding(g, seWall(c, H), 5, 0.06);
    for (const t of [0.3, 0.7]) windowOn(g, swWall(c, H), t, 21, { wpx: 8, hpx: 9 });
    windowOn(g, swWall(c, H), 0.5, 9, { wpx: 8, hpx: 9, lit: true });
    windowOn(g, seWall(c, H), 0.75, 20, { wpx: 8, hpx: 9, shaded: true });
    doorOn(g, seWall(c, 0), 0.3, 12, 15, 0x3a4a52);
    outlineBox(g, c, H);
    flatParapetRoof(g, c, H, 0xf0ece0);
    roofBox(g, c.sx / 2 - 10, c.sy / 2 - H + 2, 8, 0xc8c4b8);
    // Red crosses: big on the SE wall, small on the roof box.
    const cross = wallPoint(seWall(c, H), 0.55, 22);
    g.rect(cross.x - 2, cross.y - 6, 4, 12).fill(0xc03838);
    g.rect(cross.x - 6, cross.y - 2, 12, 4).fill(0xc03838);
    aoBase(g, seWall(c, H));
    aoBase(g, swWall(c, H));
    return { attachX: c.sx / 2, attachY: c.sy / 2 - H - 6, anim: null };
  },
  communityHall: (g, c) => {
    const H = 24;
    shadowSkirt(g, c);
    boxWalls(g, c, H, 0xb08a5c);
    siding(g, swWall(c, H), 3.5);
    siding(g, seWall(c, H), 3.5);
    // Big barn-style double door with cross bracing.
    const door = doorOn(g, seWall(c, 0), 0.4, 14, 15, 0x8a5a34);
    g.moveTo(door.x - 7, door.y - 14).lineTo(door.x + 7, door.y - 1).stroke({ width: 1.5, color: 0x5a3c22 });
    g.moveTo(door.x + 7, door.y - 15).lineTo(door.x - 7, door.y).stroke({ width: 1.5, color: 0x5a3c22 });
    windowOn(g, swWall(c, H), 0.3, 14, { wpx: 8, hpx: 8 });
    windowOn(g, swWall(c, H), 0.7, 14, { wpx: 8, hpx: 8, lit: true });
    gableRoof(g, c, H, 16, 0x8a6a44);
    signBoard(g, seWall(c, 0), 0.75, H + 4, 'HALL');
    aoBase(g, seWall(c, H));
    return { attachX: (c.ex + c.sx) / 2, attachY: (c.ey + c.sy) / 2 - H - 18, anim: null };
  },
  chapel: (g, c) => {
    const H = 24;
    shadowSkirt(g, c);
    boxWalls(g, c, H, 0xeae2d0);
    siding(g, swWall(c, H), 4, 0.08);
    siding(g, seWall(c, H), 4, 0.08);
    // Tall arched windows (drawn as narrow lit windows).
    windowOn(g, swWall(c, H), 0.35, 13, { wpx: 5, hpx: 12 });
    windowOn(g, swWall(c, H), 0.65, 13, { wpx: 5, hpx: 12, lit: true });
    windowOn(g, seWall(c, H), 0.75, 13, { wpx: 5, hpx: 12, shaded: true });
    doorOn(g, seWall(c, 0), 0.35, 9, 13, 0x5a3c28);
    gableRoof(g, c, H, 18, 0x7a6a58);
    // Steeple at the west corner: tower, spire, cross.
    const tx = c.wx / 2 + 4;
    const ty = c.wy / 2;
    g.rect(tx - 5, ty - H - 34, 10, 34).fill(0xe0d6c2);
    g.rect(tx + 1, ty - H - 34, 4, 34).fill(0xc8bda6);
    g.rect(tx - 3, ty - H - 30, 6, 7).fill(0x3a3430); // belfry opening
    g.poly([tx - 7, ty - H - 34, tx, ty - H - 48, tx + 7, ty - H - 34]).fill(0x7a6a58);
    g.rect(tx - 0.75, ty - H - 56, 1.5, 8).fill(0x8a6a3a);
    g.rect(tx - 3, ty - H - 53.5, 6, 1.5).fill(0x8a6a3a);
    aoBase(g, seWall(c, H));
    return { attachX: tx, attachY: ty - H - 48, anim: null };
  },
  townhome: (g, c) => {
    const H = 34;
    shadowSkirt(g, c);
    boxWalls(g, c, H, 0xc9a684);
    // Three rowhouse bays, each its own paint job — the SE face is repainted
    // per bay so the terrace reads as three homes, not one tan box.
    const BAYS = [0xb8756a, 0xc9a684, 0x8a9a74] as const;
    const se = seWall(c, H);
    for (const [index, [t0, t1]] of ([[0, 1 / 3], [1 / 3, 2 / 3], [2 / 3, 1]] as const).entries()) {
      const a = wallPoint(se, t0, H);
      const b = wallPoint(se, t1, H);
      const a0 = wallPoint(se, t0, 0);
      const b0 = wallPoint(se, t1, 0);
      g.poly([a.x, a.y, b.x, b.y, b0.x, b0.y, a0.x, a0.y]).fill(darken(BAYS[index]!, 0.78));
    }
    siding(g, swWall(c, H), 4, 0.1);
    siding(g, se, 4, 0.1);
    // Party walls between bays.
    for (const t of [1 / 3, 2 / 3]) {
      const top = wallPoint(se, t, H);
      const bottom = wallPoint(se, t, 0);
      g.moveTo(top.x, top.y).lineTo(bottom.x, bottom.y).stroke({ width: 1.5, color: 0x3c2c1e, alpha: 0.8 });
    }
    const ds = wallSlope(seWall(c, 0));
    for (const [index, t] of [0.17, 0.5, 0.83].entries()) {
      const door = doorOn(g, seWall(c, 0), t, 7, 12, index === 1 ? 0x4a3020 : 0x35402e);
      // Little shed awning over each door.
      g.poly(skewRect(g, door.x, door.y - 13, 12, 2.5, ds)).fill(0x6b4f36);
      windowOn(g, se, t, 24, { wpx: 7, hpx: 8, lit: index === 0, shaded: index === 2 });
    }
    windowOn(g, swWall(c, H), 0.3, 22, { wpx: 8, hpx: 9 });
    windowOn(g, swWall(c, H), 0.7, 22, { wpx: 8, hpx: 9, lit: true });
    // Tar roof, darker than the walls, with a stepped SE parapet.
    flatParapetRoof(g, c, H, 0x8a7a68);
    const roofSE = { x0: c.sx, y0: c.sy - H - 1, x1: c.ex, y1: c.ey - H - 1, h: 3 };
    fillWall(g, roofSE as Wall, 0x7a6a58);
    for (const t of [1 / 6, 0.5, 5 / 6]) {
      const p = wallPoint(roofSE as Wall, t, 3);
      g.rect(p.x - 3, p.y - 2, 6, 2).fill(0x6b5a48);
    }
    // A brick chimney per bay, spaced across the back half of the roof.
    for (const f of [0.3, 0.5, 0.7]) {
      const rx = c.wx + (c.ex - c.wx) * f;
      const ry = c.wy - H - 8;
      g.rect(rx - 2, ry - 9, 5, 9).fill(0x9a6a56);
      g.rect(rx - 3, ry - 10, 7, 2).fill(0x7a5244);
      g.rect(rx - 1, ry - 7, 1, 6).fill({ color: 0x000000, alpha: 0.2 });
    }
    outlineBox(g, c, H);
    aoBase(g, seWall(c, H));
    return { attachX: c.sx / 2, attachY: c.sy / 2 - H - 10, anim: null };
  },
  apartment: (g, c) => {
    const H = 64;
    shadowSkirt(g, c);
    boxWalls(g, c, H, 0xb59a80);
    siding(g, swWall(c, H), 6, 0.07);
    siding(g, seWall(c, H), 6, 0.07);
    // Floor bands + window grids, three storeys.
    for (const wl of [swWall(c, H), seWall(c, H)]) {
      const shaded = wl.x0 === c.sx;
      for (const band of [22, 43]) {
        g.poly([wl.x0, wl.y0 - band, wl.x1, wl.y1 - band, wl.x1, wl.y1 - band - 2, wl.x0, wl.y0 - band - 2]).fill({
          color: 0x8a705c,
          alpha: 0.9,
        });
      }
      for (const storey of [11, 32, 53]) {
        for (const t of [0.16, 0.39, 0.61, 0.84]) {
          windowOn(g, wl, t, storey, { wpx: 7, hpx: 8, shaded, lit: (storey === 32 && t === 0.39) || (storey === 11 && t === 0.84) });
        }
      }
    }
    // Brick ground storey grounds the block.
    for (const wl of [swWall(c, H), seWall(c, H)]) {
      g.poly([wl.x0, wl.y0 - 20, wl.x1, wl.y1 - 20, wl.x1, wl.y1, wl.x0, wl.y0]).fill({
        color: wl.x0 === c.sx ? 0x6e4a3c : 0x8a5c4a,
        alpha: 0.55,
      });
      siding(g, { ...wl, h: 20 }, 3, 0.1);
      for (const t of [0.16, 0.39, 0.61, 0.84]) {
        windowOn(g, wl, t, 11, { wpx: 7, hpx: 8, shaded: wl.x0 === c.sx, lit: wl.x0 !== c.sx && t === 0.84 });
      }
    }
    const door = doorOn(g, seWall(c, 0), 0.5, 11, 14, 0x3a3028);
    const ds = wallSlope(seWall(c, 0));
    g.poly(skewRect(g, door.x, door.y - 15, 15, 3, ds)).fill(0x6b5a48); // entry canopy
    g.poly(skewRect(g, door.x, door.y + 2, 17, 2, ds)).fill({ color: 0xd8c9a4, alpha: 0.6 }); // stoop
    // Tar roof crowded with city furniture: bulkhead, vents, a water tank.
    flatParapetRoof(g, c, H, 0x6e6458);
    roofBox(g, c.sx / 2 - 16, c.sy / 2 - H + 4, 10, 0x9a8570); // stair bulkhead
    roofBox(g, c.sx / 2 + 14, c.sy / 2 - H + 8, 6, 0x8a7560); // vent
    const tankX = c.sx / 2 + 2;
    const tankY = c.sy / 2 - H - 2;
    g.ellipse(tankX, tankY + 10, 8, 4).fill({ color: 0x000000, alpha: 0.22 });
    g.rect(tankX - 6, tankY + 4, 2, 6).fill(0x4a3626); // legs
    g.rect(tankX + 4, tankY + 4, 2, 6).fill(0x4a3626);
    g.rect(tankX - 7, tankY - 8, 14, 13).fill(0x8a6a4a); // stave body
    for (const off of [-4, 0, 4]) {
      g.moveTo(tankX + off, tankY - 8).lineTo(tankX + off, tankY + 5).stroke({ width: 1, color: 0x000000, alpha: 0.18 });
    }
    g.moveTo(tankX - 7, tankY - 1).lineTo(tankX + 7, tankY - 1).stroke({ width: 1, color: 0x3c2c1e, alpha: 0.6 }); // hoop
    g.ellipse(tankX, tankY - 8, 7, 3).fill(0x9a7a56);
    g.poly([tankX - 8, tankY - 8, tankX, tankY - 15, tankX + 8, tankY - 8]).fill(0x6b4f36); // conical cap
    g.rect(tankX - 0.5, tankY - 17, 1, 3).fill(0x4a3626);
    outlineBox(g, c, H);
    aoBase(g, swWall(c, H));
    aoBase(g, seWall(c, H));
    return { attachX: c.sx / 2, attachY: c.sy / 2 - H - 17, anim: null };
  },
  townHall: (g, c) => {
    const H = 42;
    shadowSkirt(g, c);
    boxWalls(g, c, H, 0xdfd0b0);
    siding(g, swWall(c, H), 5, 0.07);
    siding(g, seWall(c, H), 5, 0.07);
    // Pilasters on the SE face.
    const se = seWall(c, H);
    for (const t of [0.08, 0.92]) {
      const top = wallPoint(se, t, H - 2);
      const bottom = wallPoint(se, t, 0);
      g.moveTo(top.x, top.y).lineTo(bottom.x, bottom.y).stroke({ width: 2.5, color: 0xcbbb96 });
    }
    windowOn(g, se, 0.78, 26, { wpx: 8, hpx: 10, shaded: true });
    windowOn(g, se, 0.78, 12, { wpx: 8, hpx: 10, shaded: true });
    windowOn(g, swWall(c, H), 0.25, 26, { wpx: 8, hpx: 10 });
    windowOn(g, swWall(c, H), 0.55, 26, { wpx: 8, hpx: 10, lit: true });
    windowOn(g, swWall(c, H), 0.4, 12, { wpx: 8, hpx: 10 });
    // Projecting entry: columns carry an entablature and a real pediment
    // with the town clock set in the gable, not stuck on the door.
    const door = doorOn(g, seWall(c, 0), 0.35, 11, 15, 0x4a3626);
    const ds = wallSlope(seWall(c, 0));
    for (const off of [-9, 9]) {
      g.poly(skewRect(g, door.x + off, door.y + ds * off + 1, 3, 19, ds)).fill(0xcbbb96);
      g.poly(skewRect(g, door.x + off, door.y + ds * off + 1, 4, 2, ds)).fill(0xb8a884); // plinth
    }
    g.poly(skewRect(g, door.x, door.y - 18, 24, 3, ds)).fill(0xcbbb96); // entablature
    const pedY = door.y - 21;
    g.poly([
      door.x - 12, pedY - ds * 12,
      door.x + 12, pedY + ds * 12,
      door.x, pedY - 11,
    ]).fill(0xdfd0b0);
    g.poly([
      door.x - 12, pedY - ds * 12,
      door.x + 12, pedY + ds * 12,
      door.x, pedY - 11,
    ]).stroke({ width: 1, color: 0x8a7a5c, alpha: 0.9 });
    g.circle(door.x, pedY - 4, 3.5).fill(0xf5efe0);
    g.circle(door.x, pedY - 4, 3.5).stroke({ width: 1, color: 0x4a3626 });
    g.moveTo(door.x, pedY - 4).lineTo(door.x, pedY - 6.5).stroke({ width: 1, color: 0x4a3626 });
    g.moveTo(door.x, pedY - 4).lineTo(door.x + 2, pedY - 3.5).stroke({ width: 1, color: 0x4a3626 });
    // Slate roof with a proper cupola: plinth, windowed drum, cap, flag.
    flatParapetRoof(g, c, H, 0x8a8272);
    const cx = c.sx / 2;
    const cy = c.sy / 2 - H;
    g.poly([cx, cy - 4, cx + 9, cy + 0.5, cx, cy + 5, cx - 9, cy + 0.5]).fill(0x7a725f); // plinth
    g.rect(cx - 6, cy - 13, 12, 12).fill(0xefe5c8);
    g.rect(cx + 2, cy - 13, 4, 12).fill(0xd8ccaa); // shade side
    for (const off of [-3.5, 0.5]) g.rect(cx + off, cy - 10, 3, 6).fill(0x3a3430); // drum openings
    g.rect(cx - 7, cy - 14.5, 14, 2).fill(0xcbbb96); // drum cornice
    g.poly([cx - 8, cy - 14.5, cx, cy - 23, cx + 8, cy - 14.5]).fill(0x6e8a7a); // copper cap
    g.moveTo(cx - 4, cy - 18.5).lineTo(cx, cy - 23).stroke({ width: 1, color: 0xffffff, alpha: 0.25 });
    g.rect(cx - 0.75, cy - 32, 1.5, 9).fill(0x5a4630);
    g.poly([cx + 0.75, cy - 32, cx + 10, cy - 29, cx + 0.75, cy - 26]).fill(0xd06a4e);
    outlineBox(g, c, H);
    aoBase(g, seWall(c, H));
    return { attachX: cx, attachY: cy - 32, anim: null };
  },
  bank: (g, c) => {
    const H = 36;
    shadowSkirt(g, c);
    boxWalls(g, c, H, 0xb8b0a0);
    stonework(g, swWall(c, H), 7);
    stonework(g, seWall(c, H), 7);
    // Temple-front entry: two columns on plinths, entablature, low pediment.
    const door = doorOn(g, seWall(c, 0), 0.38, 10, 14, 0x2e2a24);
    const ds = wallSlope(seWall(c, 0));
    for (const off of [-8, 8]) {
      g.poly(skewRect(g, door.x + off, door.y + ds * off + 1, 3, 17, ds)).fill(0xcfc7b6);
      g.poly(skewRect(g, door.x + off, door.y + ds * off + 1, 4.5, 2, ds)).fill(0xb8b0a0);
      g.poly(skewRect(g, door.x + off, door.y + ds * off - 15, 4.5, 1.5, ds)).fill(0xdfd8c8); // capital
    }
    g.poly(skewRect(g, door.x, door.y - 17, 22, 3, ds)).fill(0xcfc7b6);
    g.poly([
      door.x - 11, door.y - 20 - ds * 11,
      door.x + 11, door.y - 20 + ds * 11,
      door.x, door.y - 26,
    ]).fill(0xc8c0ae);
    windowOn(g, seWall(c, H), 0.78, 20, { wpx: 6, hpx: 8, bars: true, shaded: true });
    windowOn(g, swWall(c, H), 0.3, 20, { wpx: 6, hpx: 8, bars: true });
    windowOn(g, swWall(c, H), 0.7, 20, { wpx: 6, hpx: 8, bars: true });
    // Dentil cornice under the roofline on the street face.
    const cor = seWall(c, H);
    g.poly([cor.x0, cor.y0 - H + 1, cor.x1, cor.y1 - H + 1, cor.x1, cor.y1 - H + 4, cor.x0, cor.y0 - H + 4]).fill(0xd8d0be);
    for (let t = 0.06; t < 1; t += 0.09) {
      const p = wallPoint(cor, t, H - 4);
      g.rect(p.x - 1, p.y, 2, 2).fill({ color: 0x000000, alpha: 0.3 });
    }
    flatParapetRoof(g, c, H, 0x9a9284);
    roofBox(g, c.sx / 2 + 12, c.sy / 2 - H + 6, 6, 0xa8a090);
    signBoard(g, seWall(c, 0), 0.38, H - 8, 'BANK');
    outlineBox(g, c, H);
    aoBase(g, swWall(c, H));
    aoBase(g, seWall(c, H));
    return { attachX: c.sx / 2, attachY: c.sy / 2 - H - 4, anim: null };
  },
  park: (g, c) => {
    // A town square: crossing gravel paths, a fountain, benches, flowerbeds.
    const cx = c.sx / 2;
    const cy = c.sy / 2;
    g.poly([0, 0, c.ex, c.ey, c.sx, c.sy, c.wx, c.wy]).fill(0x8fa964);
    // Mottled grass.
    for (const [mx, my, r] of [[cx - 20, cy - 6, 7], [cx + 18, cy + 2, 6], [cx - 2, cy + 14, 8], [cx + 8, cy - 16, 5]] as const) {
      g.ellipse(mx, my, r, r / 2).fill({ color: 0x7d9655, alpha: 0.6 });
    }
    // Hedge border just inside the lot line.
    g.poly([0, 3, c.ex - 5, c.ey, c.sx, c.sy - 3, c.wx + 5, c.wy]).stroke({ width: 2.5, color: 0x5c7a40, alpha: 0.9 });
    // Crossing paths along both iso diagonals, meeting at a plaza.
    for (const [ax, ay, bx2, by2] of [
      [0, 2, c.sx, c.sy - 2],
      [c.wx + 4, c.wy, c.ex - 4, c.ey],
    ] as const) {
      g.moveTo(ax, ay).lineTo(bx2, by2).stroke({ width: 8, color: 0xb8a884, alpha: 0.3 });
      g.moveTo(ax, ay).lineTo(bx2, by2).stroke({ width: 6, color: 0xd8c9a4, alpha: 0.95 });
    }
    g.ellipse(cx, cy, 11, 5.5).fill(0xd8c9a4); // plaza
    // Stone fountain with a jet.
    g.ellipse(cx, cy, 7, 3.5).fill(0xb8b0a0);
    g.ellipse(cx, cy - 0.5, 5, 2.5).fill(0x63a8da);
    g.ellipse(cx - 1.5, cy - 1, 1.5, 0.7).fill({ color: 0xffffff, alpha: 0.5 });
    g.rect(cx - 1, cy - 6, 2, 5.5).fill(0xb8b0a0);
    g.ellipse(cx, cy - 6.5, 2.5, 1).fill(0x63a8da);
    g.rect(cx - 0.5, cy - 9, 1, 2.5).fill({ color: 0xd8ecfa, alpha: 0.9 });
    g.circle(cx, cy - 9.5, 1).fill({ color: 0xd8ecfa, alpha: 0.7 });
    // Trees anchor three quadrants.
    for (const [tx, ty, s] of [[c.wx / 2 + 8, c.wy / 2 + 4, 1], [c.ex / 2 + 2, c.ey / 2 - 2, 0.85], [cx - 14, cy + 12, 0.7]] as const) {
      g.ellipse(tx, ty + 2, 6 * s, 3 * s).fill({ color: 0x000000, alpha: 0.18 });
      g.rect(tx - 1, ty - 8 * s, 2, 9 * s).fill(0x6b4f36);
      g.circle(tx, ty - 11 * s, 6 * s).fill(0x5f7d43);
      g.circle(tx - 3 * s, ty - 9 * s, 4 * s).fill(0x6f9150);
      g.circle(tx + 3 * s, ty - 9 * s, 4 * s).fill(0x54703c);
    }
    // Slatted benches facing the plaza.
    for (const [bx, by, flip] of [[cx + 14, cy - 5, 1], [cx - 16, cy + 3, -1]] as const) {
      g.ellipse(bx, by + 2, 5, 1.5).fill({ color: 0x000000, alpha: 0.15 });
      g.poly([bx - 5, by, bx + 5, by + flip * 2.5, bx + 5, by + flip * 2.5 - 2, bx - 5, by - 2]).fill(0x8a6a3a);
      g.poly([bx - 5, by - 5, bx + 5, by + flip * 2.5 - 5, bx + 5, by + flip * 2.5 - 3.5, bx - 5, by - 3.5]).fill(0x9a7a46);
      g.rect(bx - 4.5, by - 1, 1.2, 3).fill(0x4a3626);
      g.rect(bx + 3.5, by + flip * 2.5 - 1, 1.2, 3).fill(0x4a3626);
    }
    // Flowerbeds: dark soil patches stitched with blossom dots.
    for (const [fx, fy, colors] of [
      [c.wx / 2 + 20, c.wy / 2 + 12, [0xd06a4e, 0xe8c170, 0xd06a4e]],
      [c.ex / 2 - 10, c.ey / 2 + 8, [0xe8c170, 0xc888b8, 0xd06a4e]],
      [cx + 8, cy + 16, [0xc888b8, 0xd06a4e, 0xe8c170]],
    ] as const) {
      g.ellipse(fx, fy, 5, 2.4).fill(0x6b5a3c);
      colors.forEach((color, index) => {
        g.circle(fx - 3 + index * 3, fy - 0.7 + (index % 2), 0.8).fill(color);
        g.circle(fx - 1.8 + index * 3, fy + 0.6 - (index % 2), 0.6).fill(darken(color, 0.85));
      });
    }
    g.poly([0, 0, c.ex, c.ey, c.sx, c.sy, c.wx, c.wy]).stroke({ width: 1, color: 0x6b8a4a, alpha: 0.9 });
    return { attachX: cx, attachY: cy - 20, anim: null };
  },
  publicPool: (g, c) => {
    // Concrete deck with a big blue pool, ladder and umbrellas.
    g.poly([0, 0, c.ex, c.ey, c.sx, c.sy, c.wx, c.wy]).fill(0xd8d2c0);
    g.poly([0, 0, c.ex, c.ey, c.sx, c.sy, c.wx, c.wy]).stroke({ width: 1, color: 0xb8b2a0 });
    const cx = c.sx / 2;
    const cy = c.sy / 2;
    // Deck tile joints along both iso axes (under everything else).
    for (const f of [0.25, 0.5, 0.75]) {
      g.moveTo(c.wx * f, c.wy * f)
        .lineTo(c.ex + (c.sx - c.ex) * f, c.ey + (c.sy - c.ey) * f)
        .stroke({ width: 1, color: 0xb8b2a0, alpha: 0.35 });
      g.moveTo(c.ex * f, c.ey * f)
        .lineTo(c.wx + (c.sx - c.wx) * f, c.wy + (c.sy - c.wy) * f)
        .stroke({ width: 1, color: 0xb8b2a0, alpha: 0.35 });
    }
    // Pool basin: inset diamond of water.
    const inset = 0.55;
    const px = (x: number, y: number): [number, number] => [cx + (x - cx) * inset, cy + (y - cy) * inset];
    const [nx, ny] = px(0, 0);
    const [ex2, ey2] = px(c.ex, c.ey);
    const [sx2, sy2] = px(c.sx, c.sy);
    const [wx2, wy2] = px(c.wx, c.wy);
    g.poly([nx, ny, ex2, ey2, sx2, sy2, wx2, wy2]).fill(0x4d90c8);
    g.poly([nx, ny + 2, ex2 - 3, ey2, sx2, sy2 - 2, wx2 + 3, wy2]).fill(0x63a8da);
    g.poly([nx, ny, ex2, ey2, sx2, sy2, wx2, wy2]).stroke({ width: 2, color: 0xeae4d2 });
    // Sparkles.
    for (const [dx, dy] of [[-10, 2], [6, -4], [-2, 8], [12, 4], [2, -1]] as const) {
      g.rect(cx + dx, cy + dy, 3, 1).fill({ color: 0xd8ecfa, alpha: 0.8 });
    }
    // Pool ladder climbing out over the E lip.
    g.moveTo(ex2 - 3, ey2 - 5).lineTo(ex2 - 3, ey2 + 6).stroke({ width: 1.5, color: 0xf0f0e8 });
    g.moveTo(ex2 + 2, ey2 - 3).lineTo(ex2 + 2, ey2 + 8).stroke({ width: 1.5, color: 0xf0f0e8 });
    for (const step of [0, 3, 6] as const) {
      g.moveTo(ex2 - 3, ey2 - 2 + step).lineTo(ex2 + 2, ey2 + step).stroke({ width: 1, color: 0xf0f0e8 });
    }
    // Diving board springing off the W lip over the water.
    g.ellipse(wx2 + 10, wy2 + 2, 5, 1.5).fill({ color: 0x1e4668, alpha: 0.25 });
    g.rect(wx2 - 9, wy2 - 4, 18, 3).fill(0xe8e2d0);
    g.rect(wx2 - 9, wy2 - 1, 18, 1).fill({ color: 0x000000, alpha: 0.15 });
    g.rect(wx2 - 8, wy2 - 1, 2.5, 4).fill(0xb8b2a0);
    // Lounge chairs on the E deck, between the pool lip and the corner.
    for (const [lx, ly, stripe] of [[ex2 + 18, ey2 + 4, 0xd06a4e], [ex2 + 28, ey2 - 2, 0x6a9ac8]] as const) {
      g.ellipse(lx, ly + 2, 5, 1.5).fill({ color: 0x000000, alpha: 0.15 });
      g.poly([lx - 6, ly, lx + 4, ly - 5, lx + 6, ly - 3, lx - 4, ly + 2]).fill(0xe8e2d0);
      g.poly([lx - 6, ly, lx - 1, ly - 2.5, lx, ly - 1.5, lx - 5, ly + 1]).fill(stripe);
      g.poly([lx + 1, ly - 3.5, lx + 4, ly - 7, lx + 6, ly - 5, lx + 3, ly - 2]).fill(stripe);
    }
    // Proper umbrellas: panelled canopies with a tilt and a pole.
    for (const [ux, uy, colorA, colorB] of [
      [c.wx / 2 + 10, c.wy / 2 + 8, 0xd06a4e, 0xf0e6d2],
      [c.ex / 2 + 4, c.ey / 2 + 12, 0xe8c170, 0xf0e6d2],
    ] as const) {
      g.ellipse(ux + 1, uy + 1.5, 6, 2.5).fill({ color: 0x000000, alpha: 0.15 });
      g.rect(ux - 0.5, uy - 9, 1, 9.5).fill(0x8a8f98);
      const apexX = ux;
      const apexY = uy - 14;
      const rim: [number, number][] = [
        [ux - 8, uy - 8], [ux - 3, uy - 6.5], [ux + 3, uy - 6.5], [ux + 8, uy - 8],
        [ux + 5, uy - 10.5], [ux - 5, uy - 10.5],
      ];
      for (let index = 0; index < rim.length; index += 1) {
        const a = rim[index]!;
        const b = rim[(index + 1) % rim.length]!;
        g.poly([apexX, apexY, a[0], a[1], b[0], b[1]]).fill(index % 2 === 0 ? colorA : colorB);
      }
      g.rect(ux - 0.5, uy - 15.5, 1, 2).fill(0x8a8f98);
    }
    return { attachX: cx, attachY: cy - 16, anim: null };
  },
  university: (g, c) => {
    const H = 52;
    shadowSkirt(g, c);
    boxWalls(g, c, H, 0xa86048);
    siding(g, swWall(c, H), 6, 0.06);
    siding(g, seWall(c, H), 6, 0.06);
    // String course between the two window rows.
    for (const wl of [swWall(c, H), seWall(c, H)]) {
      g.moveTo(wl.x0, wl.y0 - 30).lineTo(wl.x1, wl.y1 - 30).stroke({ width: 1.5, color: 0x8a4c38, alpha: 0.8 });
      const shaded = wl.x0 === c.sx;
      for (const t of [0.15, 0.44, 0.72]) {
        windowOn(g, wl, t, 40, { wpx: 7, hpx: 9, shaded, lit: t === 0.44 && !shaded });
      }
      for (const t of wl.x0 === c.sx ? [0.72, 0.88] : [0.15, 0.44, 0.72]) {
        windowOn(g, wl, t, 19, { wpx: 7, hpx: 11, shaded, lit: false });
      }
    }
    // Portico: four columns on wide steps carrying an entablature + sign.
    const door = doorOn(g, seWall(c, 0), 0.4, 12, 16, 0x3a2c22);
    const ds = wallSlope(seWall(c, 0));
    g.poly(skewRect(g, door.x, door.y + 5, 38, 2.5, ds)).fill({ color: 0xd8c9a4, alpha: 0.85 });
    g.poly(skewRect(g, door.x, door.y + 2.5, 34, 2.5, ds)).fill({ color: 0xe4d5b0, alpha: 0.9 });
    for (const off of [-14, -5, 5, 14]) {
      g.poly(skewRect(g, door.x + off, door.y + ds * off + 1, 3, 22, ds)).fill(0xe8dcc4);
      g.poly(skewRect(g, door.x + off, door.y + ds * off - 20, 4, 1.5, ds)).fill(0xf0e6d2);
    }
    g.poly(skewRect(g, door.x, door.y - 22, 34, 4, ds)).fill(0xe8dcc4);
    signBoard(g, seWall(c, 0), 0.4, 28, 'UNIVERSITY');
    // Brick parapet roof with corner piers.
    flatParapetRoof(g, c, H, darken(0xa86048, 0.85));
    for (const [px2, py2] of [[c.wx / 2 + 4, c.wy / 2 - H + 2], [(c.ex + c.sx) / 2 - 4, (c.ey + c.sy) / 2 - H + 2]] as const) {
      g.rect(px2 - 2, py2 - 3, 4, 4).fill(0x8a4c38);
    }
    // Dome on a windowed drum, centered on the roof.
    const cx = c.sx / 2;
    const cy = c.sy / 2 - H;
    g.poly([cx, cy - 6, cx + 12, cy, cx, cy + 6, cx - 12, cy]).fill(0x8a4c38); // plinth
    g.rect(cx - 8, cy - 12, 16, 11).fill(0xe8dcc4); // drum
    g.rect(cx + 3, cy - 12, 5, 11).fill(0xd4c6a8); // drum shading
    for (const off of [-6, -1.5, 3]) g.rect(cx + off, cy - 9.5, 2.5, 6).fill(0x4a4038); // drum windows
    g.rect(cx - 9, cy - 14, 18, 2.5).fill(0xf0e6d2); // drum cornice
    g.ellipse(cx, cy - 15, 9, 3).fill(0x6e8a7a);
    g.poly([cx - 9, cy - 15, cx - 7, cy - 21, cx - 3, cy - 24.5, cx, cy - 25.5, cx + 3, cy - 24.5, cx + 7, cy - 21, cx + 9, cy - 15]).fill(0x6e8a7a); // copper dome
    g.poly([cx - 6, cy - 16, cx - 4, cy - 21, cx - 2, cy - 23.5, cx - 1, cy - 24, cx - 1, cy - 16]).fill({ color: 0xffffff, alpha: 0.18 });
    g.rect(cx - 1, cy - 29, 2, 4).fill(0xe8dcc4); // lantern
    g.rect(cx - 0.5, cy - 31, 1, 2).fill(0x5a4630);
    outlineBox(g, c, H);
    aoBase(g, swWall(c, H));
    aoBase(g, seWall(c, H));
    return { attachX: cx, attachY: cy - 31, anim: null };
  },
  railDepot: (g, c) => {
    shadowSkirt(g, c);
    const LIFT = 6; // platform slab height
    // Track bed running past the SE edge, under everything: ties then rails.
    const tS = { x: c.sx + 3, y: c.sy + 2 };
    const tE = { x: c.ex + 10, y: c.ey - 2 };
    for (let f = 0.02; f < 1; f += 0.07) {
      const px2 = tS.x + (tE.x - tS.x) * f;
      const py2 = tS.y + (tE.y - tS.y) * f;
      g.poly([px2, py2 + 1, px2 + 10, py2 + 6, px2 + 12, py2 + 5, px2 + 2, py2]).fill(0x5a4632);
    }
    for (const off of [[3, 1.5], [8, 4]] as const) {
      g.moveTo(tS.x + off[0], tS.y + off[1] + 1).lineTo(tE.x + off[0], tE.y + off[1] + 1).stroke({ width: 2, color: 0x4a4f58 });
      g.moveTo(tS.x + off[0], tS.y + off[1]).lineTo(tE.x + off[0], tE.y + off[1]).stroke({ width: 1, color: 0x9aa0a8 });
    }
    // Platform slab with a safety stripe along the track edge.
    g.poly([0, -LIFT, c.ex, c.ey - LIFT, c.sx, c.sy - LIFT, c.wx, c.wy - LIFT]).fill(0xc9bda2);
    fillWall(g, { x0: c.wx, y0: c.wy, x1: c.sx, y1: c.sy, h: LIFT }, 0xa89d84);
    fillWall(g, { x0: c.sx, y0: c.sy, x1: c.ex, y1: c.ey, h: LIFT }, 0x968b74);
    g.moveTo(c.sx + 2, c.sy - LIFT - 1).lineTo(c.ex - 2, c.ey - LIFT + 1 - 0).stroke({ width: 2, color: 0xe8dcc0, alpha: 0.85 });
    // Station house: gabled, on the NW band of the platform. Corners live in
    // tile space (u along the w-axis, v along the h-axis) so edges stay iso.
    const pt = (u: number, v: number): { x: number; y: number } => ({ x: (u - v) * 32, y: (u + v) * 16 });
    const N = pt(0.35, 0.15);
    const hcE = pt(2.9, 0.15);
    const hcS = pt(2.9, 1.7);
    const hcW = pt(0.35, 1.7);
    const hc = { ex: hcE.x, ey: hcE.y, sx: hcS.x, sy: hcS.y, wx: hcW.x, wy: hcW.y };
    const hw = 22;
    const sw: Wall = { x0: hc.wx, y0: hc.wy - LIFT, x1: hc.sx, y1: hc.sy - LIFT, h: hw };
    const se: Wall = { x0: hc.sx, y0: hc.sy - LIFT, x1: hc.ex, y1: hc.ey - LIFT, h: hw };
    fillWall(g, sw, darken(0xc9704f, 0.95));
    fillWall(g, se, darken(0xc9704f, 0.72));
    siding(g, sw, 4, 0.1);
    siding(g, se, 4, 0.1);
    windowOn(g, sw, 0.3, 12, { wpx: 8, hpx: 9 });
    windowOn(g, sw, 0.65, 12, { wpx: 8, hpx: 9, lit: true });
    windowOn(g, se, 0.2, 12, { wpx: 7, hpx: 8, shaded: true });
    doorOn(g, se, 0.45, 9, 13, 0x4a3020);
    windowOn(g, se, 0.72, 12, { wpx: 7, hpx: 8, shaded: true });
    // Gable roof, hand-rolled for the lifted sub-box.
    const rise = 12;
    const ridge1 = { x: (N.x + hc.wx) / 2, y: (N.y + hc.wy) / 2 - LIFT - hw - rise };
    const ridge2 = { x: (hc.ex + hc.sx) / 2, y: (hc.ey + hc.sy) / 2 - LIFT - hw - rise };
    const roofN: number[] = [N.x, N.y - LIFT - hw, hc.ex, hc.ey - LIFT - hw, ridge2.x, ridge2.y, ridge1.x, ridge1.y];
    const roofS: number[] = [hc.wx, hc.wy - LIFT - hw, hc.sx, hc.sy - LIFT - hw, ridge2.x, ridge2.y, ridge1.x, ridge1.y];
    g.poly(roofN).fill(darken(0x7a5a44, 1.14));
    g.poly(roofS).fill(darken(0x7a5a44, 0.78));
    for (const f of [0.35, 0.65]) {
      g.moveTo(hc.wx + (ridge1.x - hc.wx) * f, hc.wy - LIFT - hw + (ridge1.y - (hc.wy - LIFT - hw)) * f)
        .lineTo(hc.sx + (ridge2.x - hc.sx) * f, hc.sy - LIFT - hw + (ridge2.y - (hc.sy - LIFT - hw)) * f)
        .stroke({ width: 1, color: 0x000000, alpha: 0.16 });
    }
    g.moveTo(ridge1.x, ridge1.y).lineTo(ridge2.x, ridge2.y).stroke({ width: 2, color: darken(0x7a5a44, 0.6) });
    g.poly(roofN).stroke({ ...OUTLINE, alpha: 0.5 });
    // Brick chimney on the ridge.
    g.rect(ridge1.x + 14, ridge1.y + 3, 5, 10).fill(0x9a6a56);
    g.rect(ridge1.x + 13, ridge1.y + 2, 7, 2).fill(0x7a5244);
    signBoard(g, se, 0.5, hw + 8, 'DEPOT');
    // Platform canopy over the door-side stretch of the SE eave, carried on
    // posts at the platform edge.
    const lerp2 = (a: { x: number; y: number }, b: { x: number; y: number }, f: number): { x: number; y: number } => ({
      x: a.x + (b.x - a.x) * f,
      y: a.y + (b.y - a.y) * f,
    });
    const eaveA = lerp2({ x: hc.sx, y: hc.sy - LIFT - hw }, { x: hc.ex, y: hc.ey - LIFT - hw }, 0.18);
    const eaveB = lerp2({ x: hc.sx, y: hc.sy - LIFT - hw }, { x: hc.ex, y: hc.ey - LIFT - hw }, 1.02);
    const edgeA = lerp2({ x: c.sx, y: c.sy - LIFT - 15 }, { x: c.ex, y: c.ey - LIFT - 15 }, 0.36);
    const edgeB = lerp2({ x: c.sx, y: c.sy - LIFT - 15 }, { x: c.ex, y: c.ey - LIFT - 15 }, 1.0);
    g.poly([eaveA.x, eaveA.y + 3, eaveB.x, eaveB.y + 3, edgeB.x, edgeB.y, edgeA.x, edgeA.y]).fill(0x8a6a44);
    g.poly([eaveA.x, eaveA.y + 3, eaveB.x, eaveB.y + 3, edgeB.x, edgeB.y, edgeA.x, edgeA.y]).stroke({ ...OUTLINE, alpha: 0.5 });
    g.moveTo(edgeA.x, edgeA.y).lineTo(edgeB.x, edgeB.y).stroke({ width: 2, color: 0x6b4f36 });
    for (const f of [0.42, 0.68, 0.94]) {
      const px2 = c.sx + (c.ex - c.sx) * f;
      const py2 = c.sy - LIFT + (c.ey - c.sy) * f;
      g.rect(px2 - 1, py2 - 15, 2, 15).fill(0x6b4f36);
    }
    // Platform props: lamp, crates, a barrel, a waiting bench.
    const lampX = c.sx + 10;
    const lampY = c.sy - LIFT - 2 + (c.ey - c.sy) * 0.06;
    g.rect(lampX - 0.75, lampY - 22, 1.5, 22).fill(0x3c3630);
    g.rect(lampX - 2.5, lampY - 25, 5, 4).fill(0x2e2a24);
    g.rect(lampX - 1.5, lampY - 24, 3, 2.5).fill(0xf0c96a);
    crate(g, c.wx / 2 + 14, c.wy / 2 + 18 - LIFT, 8);
    crate(g, c.wx / 2 + 22, c.wy / 2 + 22 - LIFT, 6);
    barrel(g, c.wx / 2 + 30, c.wy / 2 + 20 - LIFT);
    const bx = hc.sx + 18;
    const by = hc.sy - LIFT + 6;
    g.poly([bx - 6, by, bx + 4, by - 5, bx + 4, by - 7, bx - 6, by - 2]).fill(0x8a6a3a);
    g.rect(bx - 5.5, by - 1, 1.2, 3).fill(0x4a3626);
    g.rect(bx + 2.5, by - 6, 1.2, 3).fill(0x4a3626);
    return { attachX: (hc.wx + hc.ex) / 2, attachY: ridge1.y - 6, anim: null };
  },
  canteen: (g, c) => {
    const H = 30;
    shadowSkirt(g, c);
    boxWalls(g, c, H, CREAM);
    siding(g, swWall(c, H), 5, 0.1);
    siding(g, seWall(c, H), 5, 0.1);
    // Big serving window with a striped canopy awning on the SE face.
    windowOn(g, seWall(c, H), 0.68, 13, { wpx: 14, hpx: 9, lit: true });
    const wl = seWall(c, H);
    const slope = wallSlope(wl);
    const awn = wallPoint(wl, 0.68, 22);
    const AWN_W = 24;
    for (let i = 0; i < 6; i += 1) {
      const x0 = awn.x - AWN_W / 2 + (i * AWN_W) / 6;
      const x1 = x0 + AWN_W / 6;
      const y0 = awn.y + slope * (x0 - awn.x);
      const y1 = awn.y + slope * (x1 - awn.x);
      g.poly([x0, y0, x1, y1, x1 + 2, y1 + 5, x0 + 2, y0 + 5]).fill(i % 2 === 0 ? 0xc9552e : 0xece2c8);
    }
    // Scalloped hem.
    for (let i = 0; i < 6; i += 1) {
      const x0 = awn.x - AWN_W / 2 + (i * AWN_W) / 6 + 2;
      const y0 = awn.y + slope * (x0 - 2 - awn.x) + 5;
      g.poly([x0, y0, x0 + AWN_W / 6, y0 + slope * (AWN_W / 6), x0 + AWN_W / 12, y0 + 2.5]).fill(
        i % 2 === 0 ? 0xc9552e : 0xece2c8,
      );
    }
    doorOn(g, seWall(c, 0), 0.28);
    windowOn(g, swWall(c, H), 0.3, 15, { wpx: 8, hpx: 9 });
    flatParapetRoof(g, c, H, darken(CREAM, 1.06));
    roofBox(g, c.sx / 2 + 8, c.sy / 2 - H + 1, 7, 0xcfc2a0);
    signBoard(g, seWall(c, 0), 0.32, H - 6, 'CANTEEN');
    // Chimney + door-side props.
    g.rect(c.wx + 9, c.wy - H - 12, 5, 13).fill(0x6f6f78);
    g.rect(c.wx + 8, c.wy - H - 14, 7, 3).fill(0x3f444c);
    const spot = wallPoint(seWall(c, 0), 0.06, 0);
    barrel(g, spot.x + 4, spot.y + 4);
    outlineBox(g, c, H);
    aoBase(g, swWall(c, H));
    aoBase(g, seWall(c, H));
    return { attachX: c.wx + 11, attachY: c.wy - H - 14, anim: null };
  },
  generalStore: (g, c) => {
    const H = 30;
    shadowSkirt(g, c);
    boxWalls(g, c, H, 0xbb8468);
    siding(g, swWall(c, H), 4);
    siding(g, seWall(c, H), 4);
    // Display window with goods silhouettes.
    windowOn(g, seWall(c, H), 0.72, 11, { wpx: 12, hpx: 10, lit: true });
    const disp = wallPoint(seWall(c, H), 0.72, 7);
    g.rect(disp.x - 4, disp.y - 2, 3, 4).fill({ color: 0x6b4f36, alpha: 0.8 });
    g.rect(disp.x + 1, disp.y - 3, 3, 5).fill({ color: 0x8a6a3a, alpha: 0.8 });
    doorOn(g, seWall(c, 0), 0.3);
    windowOn(g, swWall(c, H), 0.5, 16, { wpx: 8, hpx: 9 });
    outlineBox(g, c, H);
    aoBase(g, swWall(c, H));
    flatParapetRoof(g, c, H, darken(0xbb8468, 1.08));
    porchSE(g, c, H, 11, 20);
    falseFront(g, c, H, 16, 0xbb8468, 'STORE');
    const spot = wallPoint(swWall(c, 0), 0.85, 0);
    crate(g, spot.x - 3, spot.y + 3);
    crate(g, spot.x - 8, spot.y + 1, 5);
    return { attachX: (c.sx + c.ex) / 2, attachY: (c.sy + c.ey) / 2 - H - 16, anim: null };
  },
  saloon: (g, c) => {
    const H = 44;
    shadowSkirt(g, c);
    boxWalls(g, c, H, 0xb0562e);
    siding(g, swWall(c, H), 4);
    siding(g, seWall(c, H), 4);
    // Balcony rail line between floors.
    for (const wl of [seWall(c, H)]) {
      g.poly([wl.x0, wl.y0 - 23, wl.x1, wl.y1 - 23, wl.x1, wl.y1 - 25, wl.x0, wl.y0 - 25]).fill(0x8a4a2a);
      for (const f of [0.1, 0.25, 0.4, 0.55, 0.7, 0.85]) {
        const p = wallPoint(wl, f, 21);
        g.rect(p.x - 0.5, p.y, 1, 3).fill(0x8a4a2a);
      }
    }
    // Upstairs windows (one lit), downstairs saloon windows.
    windowOn(g, seWall(c, H), 0.3, 33, { wpx: 8, hpx: 9, lit: true });
    windowOn(g, seWall(c, H), 0.7, 33, { wpx: 8, hpx: 9, shaded: true });
    windowOn(g, seWall(c, H), 0.75, 12, { wpx: 10, hpx: 9, lit: true });
    windowOn(g, swWall(c, H), 0.4, 34, { wpx: 8, hpx: 9 });
    windowOn(g, swWall(c, H), 0.4, 13, { wpx: 9, hpx: 10, lit: true });
    // Swinging half-doors.
    const door = doorOn(g, seWall(c, 0), 0.32, 10, 15, 0x241a12);
    const ds = wallSlope(seWall(c, 0));
    g.poly(skewRect(g, door.x - 2.5, door.y - 4, 4, 7, ds)).fill(0x9a6a3e);
    g.poly(skewRect(g, door.x + 2.5, door.y - 4 + ds * 5, 4, 7, ds)).fill(0x9a6a3e);
    outlineBox(g, c, H);
    aoBase(g, swWall(c, H));
    flatParapetRoof(g, c, H, darken(0xb0562e, 1.05));
    porchSE(g, c, H, 12, 21);
    falseFront(g, c, H, 15, 0xb0562e, 'SALOON');
    // Animated hanging sign bracket on the porch corner.
    const bx = wallPoint(seWall(c, 0), 0.95, 0);
    g.rect(bx.x + 2, bx.y - 30, 8, 2).fill(0x2a1f16);
    return { attachX: (c.sx + c.ex) / 2, attachY: (c.sy + c.ey) / 2 - H - 15, anim: { kind: 'sign', x: bx.x + 9, y: bx.y - 29 } };
  },
  casino: (g, c) => {
    const H = 58;
    shadowSkirt(g, c);
    boxWalls(g, c, H, 0x93406c);
    siding(g, swWall(c, H), 5, 0.08);
    siding(g, seWall(c, H), 5, 0.08);
    // Marquee band with chase lights, both faces.
    for (const wl of [swWall(c, H), seWall(c, H)]) {
      g.poly([wl.x0, wl.y0 - 44, wl.x1, wl.y1 - 44, wl.x1, wl.y1 - 37, wl.x0, wl.y0 - 37]).fill(0xe8c170);
      const length = Math.hypot(wl.x1 - wl.x0, wl.y1 - wl.y0);
      for (let d = 5; d < length - 3; d += 6) {
        const p = wallPoint(wl, d / length, 40.5);
        g.circle(p.x, p.y, 1.1).fill(0xfff2c8);
      }
    }
    // Two window rows + red-carpet entry.
    for (const t of [0.22, 0.5, 0.78]) windowOn(g, seWall(c, H), t, 24, { wpx: 8, hpx: 10, shaded: true, lit: t === 0.5 });
    for (const t of [0.35, 0.65]) windowOn(g, seWall(c, H), t, 52, { wpx: 7, hpx: 8, shaded: true, lit: t === 0.65 });
    for (const t of [0.3, 0.7]) {
      windowOn(g, swWall(c, H), t, 24, { wpx: 8, hpx: 10 });
      windowOn(g, swWall(c, H), t, 52, { wpx: 7, hpx: 8, lit: t === 0.3 });
    }
    const door = doorOn(g, seWall(c, 0), 0.5, 12, 16, 0x3a1626);
    const ds = wallSlope(seWall(c, 0));
    g.poly(skewRect(g, door.x, door.y + 5, 16, 3, ds)).fill(0xb03040);
    outlineBox(g, c, H);
    aoBase(g, swWall(c, H));
    aoBase(g, seWall(c, H));
    flatParapetRoof(g, c, H, darken(0x93406c, 1.08));
    // Rooftop billboard: flat-facing lit board on struts.
    const bbx = c.sx / 2;
    const bby = c.sy / 2 - H - 2;
    g.rect(bbx - 20, bby - 16, 3, 18).fill(0x5a4630);
    g.rect(bbx + 17, bby - 16, 3, 18).fill(0x5a4630);
    g.moveTo(bbx - 18, bby - 2).lineTo(bbx - 26, bby + 6).stroke({ width: 1.5, color: 0x5a4630 });
    g.moveTo(bbx + 18, bby - 2).lineTo(bbx + 26, bby + 6).stroke({ width: 1.5, color: 0x5a4630 });
    g.rect(bbx - 28, bby - 34, 56, 20).fill(0x2a1a24);
    g.rect(bbx - 28, bby - 34, 56, 20).stroke({ width: 1.5, color: 0xe8c170 });
    pixelText(g, 'CASINO', bbx - 22, bby - 31, 0xf2c14e, 2);
    for (let i = 0; i < 7; i += 1) {
      g.circle(bbx - 24 + i * 8, bby - 36, 1.1).fill(0xfff2c8);
    }
    star(g, bbx + 22, bby - 40, 4, 0xd05050);
    falseFront(g, c, H, 18, 0x93406c, 'CASINO');
    return { attachX: c.sx, attachY: c.sy - H - 18, anim: null };
  },
  sheriff: (g, c) => {
    const H = 34;
    shadowSkirt(g, c);
    boxWalls(g, c, H, 0x9aa27a);
    stonework(g, swWall(c, H), 6);
    stonework(g, seWall(c, H), 6);
    // High barred windows.
    windowOn(g, swWall(c, H), 0.3, 24, { wpx: 7, hpx: 7, bars: true });
    windowOn(g, swWall(c, H), 0.7, 24, { wpx: 7, hpx: 7, bars: true });
    windowOn(g, seWall(c, H), 0.72, 24, { wpx: 7, hpx: 7, bars: true, shaded: true });
    // Heavy braced door + star above.
    const door = doorOn(g, seWall(c, 0), 0.32, 10, 15, 0x4a3a2a);
    g.moveTo(door.x - 5, door.y - 12).lineTo(door.x + 5, door.y - 4).stroke({ width: 1, color: 0x2a1f16 });
    star(g, door.x, door.y - 21, 4.5, 0xe8c170);
    pixelText(g, 'JAIL', wallPoint(swWall(c, H), 0.36, 14).x - 7, wallPoint(swWall(c, H), 0.36, 14).y - 2, 0x3a3a2a);
    flatParapetRoof(g, c, H, darken(0x9aa27a, 1.1));
    outlineBox(g, c, H);
    aoBase(g, swWall(c, H));
    aoBase(g, seWall(c, H));
    return { attachX: 0, attachY: -H - 4, anim: null };
  },
  oreDepot: (g, c) => {
    const H = 16;
    shadowSkirt(g, c);
    // Timber crib walls with beam ends crossing at corners.
    boxWalls(g, c, H, WOOD_DARK);
    siding(g, swWall(c, H), 3, 0.25);
    siding(g, seWall(c, H), 3, 0.25);
    for (const [px, py] of [[c.sx, c.sy], [c.ex, c.ey], [c.wx, c.wy]] as const) {
      for (let up = 2; up < H; up += 4) {
        g.rect(px - 2, py - up - 2, 4, 2).fill(0x8a6a3a);
      }
    }
    // Interior + a heap that actually fills the bin.
    g.poly([0, -H + 4, c.ex - 6, c.ey - H + 1, c.sx, c.sy - H - 2, c.wx + 6, c.wy - H + 1]).fill(0x453630);
    const hx = c.sx / 2;
    const hy = c.sy / 2 - H + 2;
    g.poly([hx - 30, hy + 2, hx - 12, hy - 10, hx + 4, hy - 14, hx + 22, hy - 8, hx + 30, hy + 2, hx + 14, hy + 8, hx - 14, hy + 8]).fill(
      0x6b5648,
    );
    g.poly([hx - 12, hy - 10, hx + 4, hy - 14, hx + 22, hy - 8, hx + 6, hy - 4]).fill(0x7d685c);
    for (const [dx, dy] of [[-18, 0], [-8, -5], [2, -9], [12, -4], [20, 0], [-2, 2], [8, 4], [-12, 5]] as const) {
      g.circle(hx + dx, hy + dy, 2).fill(0x846e60);
      g.rect(hx + dx, hy + dy - 1.5, 1.5, 1.5).fill(0xc4b29c);
    }
    // Corrugated lean-to over the weigh desk, posted to the west wall.
    const shed = { x: c.wx + 10, y: c.wy + 1 };
    g.rect(shed.x - 6, shed.y - 12, 2, 13).fill(0x6b4f36);
    g.rect(shed.x + 12, shed.y - 16, 2, 13).fill(0x6b4f36);
    g.poly([shed.x - 8, shed.y - 12, shed.x + 14, shed.y - 17, shed.x + 17, shed.y - 9, shed.x - 5, shed.y - 4]).fill(0x8a8f98);
    g.poly([shed.x - 8, shed.y - 12, shed.x + 14, shed.y - 17, shed.x + 17, shed.y - 9, shed.x - 5, shed.y - 4]).stroke({
      width: 1,
      color: 0x000000,
      alpha: 0.3,
    });
    g.rect(shed.x - 2, shed.y - 6, 8, 5).fill(0x9a8468);
    // Loading chute: two rails + slats angling off the SE wall.
    const chute = wallPoint(seWall(c, 0), 0.72, H - 4);
    g.moveTo(chute.x - 3, chute.y).lineTo(chute.x + 8, chute.y + 12).stroke({ width: 2, color: 0x8a6a3a });
    g.moveTo(chute.x + 3, chute.y - 2).lineTo(chute.x + 14, chute.y + 9).stroke({ width: 2, color: 0x8a6a3a });
    for (let i = 1; i <= 3; i += 1) {
      const t = i / 4;
      g.moveTo(chute.x - 3 + (11 * i) / 4, chute.y + 12 * t)
        .lineTo(chute.x + 3 + (11 * i) / 4, chute.y - 2 + 11 * t)
        .stroke({ width: 1.5, color: 0x6b4f36 });
    }
    outlineBox(g, c, H);
    return { attachX: c.sx / 2, attachY: c.sy / 2 - H - 8, anim: null };
  },
  generator: (g, c) => {
    const H = 28;
    shadowSkirt(g, c);
    boxWalls(g, c, H, STEEL);
    corrugation(g, swWall(c, H), 4);
    corrugation(g, seWall(c, H), 4);
    // Intake louvres on the SW face.
    for (let index = 0; index < 3; index += 1) {
      const p = wallPoint(swWall(c, H), 0.62, 20 - index * 4);
      g.poly(skewRect(g, p.x, p.y, 14, 2, wallSlope(swWall(c, H)))).fill({ color: 0x3f444c, alpha: 0.8 });
    }
    // Hazard band along the SE base: dark strip with angled yellow stripes.
    const se = seWall(c, H);
    const seLength = Math.hypot(se.x1 - se.x0, se.y1 - se.y0);
    const band0 = wallPoint(se, 0, 9);
    const band1 = wallPoint(se, 1, 9);
    g.poly([band0.x, band0.y, band1.x, band1.y, band1.x, band1.y + 6, band0.x, band0.y + 6]).fill(0x2a2a2e);
    for (let d = 3; d < seLength - 5; d += 7) {
      const p0 = wallPoint(se, d / seLength, 9);
      const p1 = wallPoint(se, (d + 3) / seLength, 9);
      g.poly([p0.x, p0.y, p1.x, p1.y, p1.x - 2, p1.y + 6, p0.x - 2, p0.y + 6]).fill(0xe8c170);
    }
    flatParapetRoof(g, c, H, darken(STEEL, 1.1));
    roofBox(g, c.sx / 2 + 10, c.sy / 2 - H + 2, 8, 0x7d828c);
    roofBox(g, c.sx / 2 - 4, c.sy / 2 - H + 6, 6, 0x8a959e);
    // Big stack with collar + guy wires.
    const stx = c.wx + 13;
    const sty = c.wy - H + 2;
    g.rect(stx - 3, sty - 26, 6, 26).fill(0x6a6f78);
    g.rect(stx - 3, sty - 26, 2.5, 26).fill(0x7d828c);
    g.rect(stx - 4, sty - 28, 8, 3).fill(0x3f444c);
    g.rect(stx - 3.5, sty - 16, 7, 2).fill(0x3f444c);
    g.moveTo(stx - 3, sty - 24).lineTo(stx - 14, sty + 4).stroke({ width: 1, color: 0x2a2a2e, alpha: 0.6 });
    g.moveTo(stx + 3, sty - 24).lineTo(stx + 13, sty + 6).stroke({ width: 1, color: 0x2a2a2e, alpha: 0.6 });
    // Fuel drums by the south corner.
    barrel(g, c.sx - 8, c.sy - 1);
    barrel(g, c.sx - 2, c.sy + 2);
    outlineBox(g, c, H);
    aoBase(g, swWall(c, H));
    aoBase(g, seWall(c, H));
    return { attachX: stx, attachY: sty - 28, anim: { kind: 'exhaust', x: stx, y: sty - 28 } };
  },
  powerPole: (g) => {
    g.poly([-6, 18, 0, 21, 6, 18, 0, 15]).fill({ color: 0x000000, alpha: 0.2 });
    g.rect(-2, -44, 4, 62).fill(0x7a5c40);
    g.rect(-2, -44, 1.5, 62).fill(0x8f6f4e);
    g.rect(-12, -40, 24, 3).fill(0x6b4f36);
    g.rect(-9, -32, 18, 2.5).fill(0x6b4f36);
    g.circle(-10, -41, 1.5).fill(0x3f6f8f);
    g.circle(10, -41, 1.5).fill(0x3f6f8f);
    g.circle(-7, -33, 1.5).fill(0x3f6f8f);
    g.circle(7, -33, 1.5).fill(0x3f6f8f);
    return { attachX: 0, attachY: -40, anim: null };
  },
  waterTank: (g, c) => {
    const cx = c.sx / 2;
    const cy = c.sy / 2;
    g.ellipse(cx, cy + 2, 16, 8).fill({ color: 0x000000, alpha: 0.2 });
    for (const [lx, ly] of [[-10, -2], [10, -2], [-5, 4], [5, 4]] as const) {
      g.rect(cx + lx - 1.5, cy + ly - 14, 3, 16).fill(0x5a4630);
    }
    g.rect(cx - 13, cy - 38, 26, 26).fill(0x6fa3c8);
    g.rect(cx + 3, cy - 38, 10, 26).fill(0x5b8cb0);
    g.ellipse(cx, cy - 12, 13, 6).fill(0x5b8cb0);
    g.ellipse(cx, cy - 38, 13, 6).fill(0x8fc0dd);
    g.ellipse(cx, cy - 38, 13, 6).stroke(OUTLINE);
    g.moveTo(cx - 13, cy - 30).lineTo(cx + 13, cy - 30).stroke({ width: 1.5, color: 0x3f6f8f });
    g.moveTo(cx - 13, cy - 20).lineTo(cx + 13, cy - 20).stroke({ width: 1.5, color: 0x3f6f8f });
    return { attachX: cx, attachY: cy - 42, anim: { kind: 'drip', x: cx + 10, y: cy - 10 } };
  },
  well: (g, c) => {
    const cx = c.sx / 2;
    const cy = c.sy / 2;
    g.ellipse(cx, cy + 2, 14, 7).fill({ color: 0x000000, alpha: 0.2 });
    const apexY = cy - 40;
    for (const [lx, ly] of [[-12, 0], [12, 0], [-6, 6], [6, 6]] as const) {
      g.moveTo(cx + lx, cy + ly).lineTo(cx, apexY).stroke({ width: 2, color: 0x8a6a3a });
    }
    g.moveTo(cx - 8, cy - 14).lineTo(cx + 8, cy - 14).stroke({ width: 1.5, color: 0x6b4f36 });
    g.moveTo(cx - 5, cy - 26).lineTo(cx + 5, cy - 26).stroke({ width: 1.5, color: 0x6b4f36 });
    g.circle(cx, apexY, 2.5).fill(0x5a5f68);
    g.rect(cx - 8, cy - 8, 16, 10).fill(STEEL);
    g.rect(cx - 8, cy - 8, 16, 3).fill(0xa8adb5);
    return { attachX: cx, attachY: apexY, anim: null };
  },
  cornFarm: (g, c) => {
    const cx = c.sx / 2;
    const cy = c.sy / 2;
    // Tilled lot with mottled soil.
    g.poly([0, 0, c.ex, c.ey, c.sx, c.sy, c.wx, c.wy]).fill(0x9a7a52);
    for (const [mx, my, r] of [[cx - 22, cy - 4, 8], [cx + 16, cy + 6, 7], [cx - 4, cy + 16, 6]] as const) {
      g.ellipse(mx, my, r, r / 2).fill({ color: 0x8a6a44, alpha: 0.6 });
    }
    // Corn rows parallel to the NE edge: green stalk lines dotted with tassels.
    for (const f of [0.2, 0.34, 0.48, 0.62, 0.76]) {
      const ax = c.wx * f;
      const ay = c.wy * f;
      const bx = c.ex + (c.sx - c.ex) * f;
      const by = c.ey + (c.sy - c.ey) * f;
      g.moveTo(ax, ay).lineTo(bx, by).stroke({ width: 5, color: 0x5f7d43, alpha: 0.95 });
      g.moveTo(ax, ay).lineTo(bx, by).stroke({ width: 2, color: 0x6f9150, alpha: 0.9 });
      for (const t of [0.15, 0.35, 0.55, 0.75, 0.92]) {
        g.circle(ax + (bx - ax) * t, ay + (by - ay) * t - 2, 1.2).fill(0xe8c170);
      }
    }
    // Lean-to tool shed tucked by the west corner.
    const shed = { x: c.wx + 14, y: c.wy + 2 };
    g.rect(shed.x - 7, shed.y - 11, 2, 12).fill(0x6b4f36);
    g.rect(shed.x + 11, shed.y - 15, 2, 12).fill(0x6b4f36);
    g.poly([shed.x - 9, shed.y - 11, shed.x + 13, shed.y - 16, shed.x + 16, shed.y - 8, shed.x - 6, shed.y - 3]).fill(WOOD_DARK);
    g.poly([shed.x - 9, shed.y - 11, shed.x + 13, shed.y - 16, shed.x + 16, shed.y - 8, shed.x - 6, shed.y - 3]).stroke({
      width: 1,
      color: 0x000000,
      alpha: 0.3,
    });
    barrel(g, shed.x + 20, shed.y + 2);
    // Windpump by the east corner — a desert farm advertises its water.
    const wx = c.ex / 2 + 14;
    const wy = c.ey / 2 + 10;
    const apexY = wy - 30;
    g.ellipse(wx, wy + 2, 8, 4).fill({ color: 0x000000, alpha: 0.18 });
    for (const [lx, ly] of [[-7, 0], [7, 0], [-3, 4], [3, 4]] as const) {
      g.moveTo(wx + lx, wy + ly).lineTo(wx, apexY).stroke({ width: 1.5, color: 0x8a6a3a });
    }
    g.moveTo(wx - 4, wy - 12).lineTo(wx + 4, wy - 12).stroke({ width: 1, color: 0x6b4f36 });
    for (let blade = 0; blade < 6; blade += 1) {
      const angle = (blade / 6) * Math.PI * 2;
      g.moveTo(wx, apexY).lineTo(wx + Math.cos(angle) * 6, apexY + Math.sin(angle) * 6).stroke({ width: 1.5, color: 0xd8d2c0 });
    }
    g.circle(wx, apexY, 1.5).fill(0x5a5f68);
    // Scarecrow amid the rows.
    g.rect(cx + 6, cy - 10, 1.5, 10).fill(0x6b4f36);
    g.moveTo(cx + 2, cy - 8).lineTo(cx + 11, cy - 8).stroke({ width: 1.5, color: 0x6b4f36 });
    g.circle(cx + 6.7, cy - 11, 2).fill(0xd9c27a);
    g.poly([0, 0, c.ex, c.ey, c.sx, c.sy, c.wx, c.wy]).stroke({ width: 1, color: 0x7a5c40, alpha: 0.9 });
    return { attachX: shed.x + 2, attachY: shed.y - 18, anim: null };
  },
  wheatFarm: (g, c) => {
    const cx = c.sx / 2;
    const cy = c.sy / 2;
    // A standing crop: solid gold field, not tilled rows.
    g.poly([0, 0, c.ex, c.ey, c.sx, c.sy, c.wx, c.wy]).fill(0xd9c27a);
    for (const [mx, my, r] of [[cx - 18, cy + 2, 9], [cx + 14, cy - 6, 7], [cx + 2, cy + 14, 8]] as const) {
      g.ellipse(mx, my, r, r / 2).fill({ color: 0xcbb268, alpha: 0.7 });
    }
    // Swaying grain texture: short stalk ticks parallel to the NW edge.
    for (const f of [0.18, 0.32, 0.46, 0.6, 0.74, 0.88]) {
      const ax = c.ex * f;
      const ay = c.ey * f;
      const bx = c.wx + (c.sx - c.wx) * f;
      const by = c.wy + (c.sy - c.wy) * f;
      for (const t of [0.12, 0.3, 0.48, 0.66, 0.84]) {
        const px = ax + (bx - ax) * t;
        const py = ay + (by - ay) * t;
        g.moveTo(px, py).lineTo(px + 1, py - 4).stroke({ width: 1, color: 0xe8dcae, alpha: 0.9 });
      }
    }
    // Harvested wedge by the south corner, with sheaves.
    g.poly([cx - 20, cy + 16, cx + 20, cy + 16, c.sx + 12, c.sy - 6, c.sx - 12, c.sy - 6]).fill(0xc0a860);
    for (const [hx, hy] of [[cx - 8, cy + 22], [cx + 6, cy + 26]] as const) {
      g.poly([hx - 3, hy, hx, hy - 7, hx + 3, hy]).fill(0xb89a52);
      g.moveTo(hx - 2, hy - 2).lineTo(hx + 2, hy - 2).stroke({ width: 1, color: 0x8a6a3a });
    }
    // Irrigation channel along the NE edge — wheat gulps water out here.
    g.moveTo(c.ex * 0.94, c.ey * 0.94)
      .lineTo(c.ex + (c.sx - c.ex) * 0.06 - 4, c.ey + (c.sy - c.ey) * 0.06)
      .stroke({ width: 3, color: 0x63a8da, alpha: 0.9 });
    // Lean-to shed on the west corner.
    const shed = { x: c.wx + 12, y: c.wy + 3 };
    g.rect(shed.x - 6, shed.y - 10, 2, 11).fill(0x6b4f36);
    g.rect(shed.x + 10, shed.y - 14, 2, 11).fill(0x6b4f36);
    g.poly([shed.x - 8, shed.y - 10, shed.x + 12, shed.y - 15, shed.x + 15, shed.y - 7, shed.x - 5, shed.y - 2]).fill(WOOD_DARK);
    crate(g, shed.x + 20, shed.y + 3, 6);
    g.poly([0, 0, c.ex, c.ey, c.sx, c.sy, c.wx, c.wy]).stroke({ width: 1, color: 0xa8905c, alpha: 0.9 });
    return { attachX: shed.x + 2, attachY: shed.y - 17, anim: null };
  },
  cattleRanch: (g, c) => {
    const cx = c.sx / 2;
    const cy = c.sy / 2;
    // Hoof-worn dirt lot.
    g.poly([0, 0, c.ex, c.ey, c.sx, c.sy, c.wx, c.wy]).fill(0xb09468);
    for (const [mx, my, r] of [[cx - 14, cy + 4, 8], [cx + 12, cy - 2, 6], [cx - 2, cy + 14, 7]] as const) {
      g.ellipse(mx, my, r, r / 2).fill({ color: 0x9e8258, alpha: 0.6 });
    }
    // Perimeter fence: posts with two rails, all four sides.
    const cornerPts = [
      [0, 0],
      [c.ex, c.ey],
      [c.sx, c.sy],
      [c.wx, c.wy],
    ] as const;
    for (let side = 0; side < 4; side += 1) {
      const [ax, ay] = cornerPts[side]!;
      const [bx, by] = cornerPts[(side + 1) % 4]!;
      for (const rail of [4, 8]) {
        g.moveTo(ax, ay - rail).lineTo(bx, by - rail).stroke({ width: 1, color: 0x6b4f36, alpha: 0.9 });
      }
      for (const t of [0.1, 0.3, 0.5, 0.7, 0.9]) {
        const px = ax + (bx - ax) * t;
        const py = ay + (by - ay) * t;
        g.rect(px - 1, py - 10, 2, 10).fill(0x5a4630);
      }
    }
    // Gate gap on the SE side.
    const gx = c.sx + (c.ex - c.sx) * 0.5;
    const gy = c.sy + (c.ey - c.sy) * 0.5;
    g.rect(gx - 6, gy - 12, 12, 12).fill(0xb09468);
    // Water trough + corn feed bunk against the north fence.
    g.rect(c.ex / 2 - 12, c.ey / 2 + 2, 14, 5).fill(0x6b4f36);
    g.rect(c.ex / 2 - 11, c.ey / 2 + 3, 12, 3).fill(0x63a8da);
    g.rect(c.wx / 2 + 2, c.wy / 2 + 4, 14, 5).fill(0x6b4f36);
    for (const dot of [2, 5, 8, 11] as const) {
      g.circle(c.wx / 2 + 2 + dot, c.wy / 2 + 5.5, 1.2).fill(0xe8c170);
    }
    // The herd: blocky longhorns, sun side lighter.
    for (const [bx, by, flip, tone] of [
      [cx - 8, cy - 2, 1, 0x8a6a4a],
      [cx + 10, cy + 8, -1, 0x7a5a40],
      [cx - 18, cy + 12, 1, 0x9a7a56],
    ] as const) {
      g.ellipse(bx, by + 3, 7, 2).fill({ color: 0x000000, alpha: 0.15 });
      g.ellipse(bx, by - 3, 6.5, 4).fill(tone);
      g.ellipse(bx - 2 * flip, by - 4.5, 3.5, 2.2).fill(darken(tone, 1.12));
      // Head + horns.
      const hx = bx + 6.5 * flip;
      g.ellipse(hx, by - 4, 2.5, 2).fill(darken(tone, 0.9));
      g.moveTo(hx - 3, by - 6).lineTo(hx + 3, by - 6).stroke({ width: 1.2, color: 0xe4d5b0 });
      // Legs.
      for (const off of [-4, -1.5, 1.5, 4] as const) {
        g.rect(bx + off, by, 1.2, 4).fill(darken(tone, 0.75));
      }
    }
    // Loafing shed on the west corner.
    const shed = { x: c.wx + 16, y: c.wy + 6 };
    g.rect(shed.x - 8, shed.y - 12, 2, 13).fill(0x6b4f36);
    g.rect(shed.x + 12, shed.y - 16, 2, 13).fill(0x6b4f36);
    g.poly([shed.x - 10, shed.y - 12, shed.x + 14, shed.y - 17, shed.x + 17, shed.y - 9, shed.x - 7, shed.y - 4]).fill(ROOF_BROWN);
    g.poly([shed.x - 10, shed.y - 12, shed.x + 14, shed.y - 17, shed.x + 17, shed.y - 9, shed.x - 7, shed.y - 4]).stroke({
      width: 1,
      color: 0x000000,
      alpha: 0.3,
    });
    g.poly([0, 0, c.ex, c.ey, c.sx, c.sy, c.wx, c.wy]).stroke({ width: 1, color: 0x8a7048, alpha: 0.9 });
    return { attachX: shed.x + 2, attachY: shed.y - 19, anim: null };
  },
  warehouse: (g, c) => {
    const H = 30;
    shadowSkirt(g, c);
    boxWalls(g, c, H, STEEL);
    corrugation(g, swWall(c, H), 4);
    corrugation(g, seWall(c, H), 4);
    // Big sliding freight door on the SE face, with overhead track.
    const se = seWall(c, H);
    const door = wallPoint(se, 0.4, 0);
    g.poly(skewRect(g, door.x, door.y, 22, 18, wallSlope(se))).fill(0x6a6f78);
    g.poly(skewRect(g, door.x - 2, door.y, 16, 16, wallSlope(se))).fill(0x7d828c);
    g.moveTo(door.x - 13, door.y - 19 - wallSlope(se) * 13)
      .lineTo(door.x + 13, door.y - 19 + wallSlope(se) * 13)
      .stroke({ width: 2, color: 0x3f444c });
    // High vent windows under the roofline.
    windowOn(g, swWall(c, H), 0.3, H - 8, { wpx: 7, hpx: 6, shaded: true });
    windowOn(g, swWall(c, H), 0.7, H - 8, { wpx: 7, hpx: 6, shaded: true });
    flatParapetRoof(g, c, H, darken(STEEL, 1.08));
    roofBox(g, c.sx / 2 + 8, c.sy / 2 - H + 3, 7, 0x8a959e);
    outlineBox(g, c, H);
    signBoard(g, seWall(c, 0), 0.75, H - 7, 'STORAGE');
    // Stacked goods waiting on the apron.
    crate(g, c.sx - 10, c.sy + 2, 7);
    crate(g, c.sx - 3, c.sy + 4, 6);
    crate(g, c.sx - 7, c.sy - 3, 5);
    barrel(g, c.ex - 4, c.ey + 4);
    aoBase(g, swWall(c, H));
    aoBase(g, seWall(c, H));
    return { attachX: c.sx / 2, attachY: c.sy / 2 - H - 4, anim: null };
  },
  freightOffice: (g, c) => {
    const H = 24;
    shadowSkirt(g, c);
    boxWalls(g, c, H, CREAM);
    siding(g, swWall(c, H), 4);
    siding(g, seWall(c, H), 4);
    falseFront(g, c, H, 12, CREAM, 'FREIGHT');
    doorOn(g, seWall(c, 0), 0.28, 8, 13);
    windowOn(g, seWall(c, H), 0.68, 12, { lit: true });
    windowOn(g, swWall(c, H), 0.5, 12, {});
    // Dock scale + waybill desk against the SW wall.
    const desk = wallPoint(swWall(c, 0), 0.72, 0);
    g.rect(desk.x - 5, desk.y - 7, 10, 7).fill(0x9a8468);
    g.rect(desk.x - 5, desk.y - 8, 10, 2).fill(0xb09a78);
    // Outbound freight staged by the south corner.
    crate(g, c.sx - 8, c.sy + 1, 7);
    crate(g, c.sx + 1, c.sy + 3, 5);
    barrel(g, c.sx - 14, c.sy + 4);
    outlineBox(g, c, H);
    aoBase(g, seWall(c, H));
    return { attachX: c.sx / 2, attachY: c.sy / 2 - H - 14, anim: null };
  },
  goodsMarket: (g, c) => {
    const cx = c.sx / 2;
    const cy = c.sy / 2;
    // Paved market floor.
    g.poly([0, 0, c.ex, c.ey, c.sx, c.sy, c.wx, c.wy]).fill(0xd8c9a4);
    g.poly([0, 0, c.ex, c.ey, c.sx, c.sy, c.wx, c.wy]).stroke({ width: 1, color: 0xb8a884 });
    for (const f of [0.33, 0.66]) {
      g.moveTo(c.wx * f, c.wy * f)
        .lineTo(c.ex + (c.sx - c.ex) * f, c.ey + (c.sy - c.ey) * f)
        .stroke({ width: 1, color: 0xb8a884, alpha: 0.4 });
    }
    // Open pavilion: six posts holding a gabled canopy.
    const H = 18;
    for (const [px, py] of [
      [c.wx + 4, c.wy + 2],
      [c.sx - 30, c.sy - 13],
      [c.sx + 2, c.sy - 2],
      [c.ex - 6, c.ey + 2],
      [c.ex - 34, c.ey - 14],
      [4, 2],
    ] as const) {
      g.rect(px - 1.5, py - H, 3, H).fill(0x6b4f36);
      g.ellipse(px, py + 1, 3, 1.2).fill({ color: 0x000000, alpha: 0.2 });
    }
    gableRoof(g, c, H, 9, 0xc86a50);
    // Produce stalls under the canopy: corn heap, wheat sacks, crated glassware.
    g.rect(cx - 20, cy - 6, 13, 6).fill(0x8a6a3a);
    for (const [dx, dy] of [[-17, -7], [-13, -9], [-9, -7], [-15, -5], [-11, -5]] as const) {
      g.circle(cx + dx, cy + dy, 1.6).fill(0xe8c170);
    }
    for (const [sx2, sy2] of [[cx + 4, cy + 2], [cx + 10, cy + 4]] as const) {
      g.ellipse(sx2, sy2, 3, 3.6).fill(0xd9c27a);
      g.moveTo(sx2 - 2, sy2 - 3).lineTo(sx2 + 2, sy2 - 3).stroke({ width: 1, color: 0x8a6a3a });
    }
    crate(g, cx - 2, cy + 12, 7);
    crate(g, cx + 6, cy + 14, 5);
    barrel(g, cx + 18, cy + 6);
    signBoard(g, seWall(c, 0), 0.5, H + 14, 'MARKET');
    return { attachX: cx, attachY: cy - H - 14, anim: null };
  },
  glassworks: (g, c) => {
    const H = 30;
    shadowSkirt(g, c);
    boxWalls(g, c, H, 0xb87a5e);
    stonework(g, swWall(c, H), 6);
    stonework(g, seWall(c, H), 6);
    // The furnace mouth glows on the SE face.
    const se = seWall(c, H);
    const mouth = wallPoint(se, 0.35, 0);
    g.poly(skewRect(g, mouth.x, mouth.y, 14, 12, wallSlope(se))).fill(0x4a3626);
    g.poly(skewRect(g, mouth.x, mouth.y, 10, 9, wallSlope(se))).fill(0xe8843a);
    g.poly(skewRect(g, mouth.x, mouth.y, 6, 6, wallSlope(se))).fill(0xf5c04e);
    windowOn(g, se, 0.72, H - 10, { wpx: 7, hpx: 6, shaded: true });
    windowOn(g, swWall(c, H), 0.5, H - 10, { wpx: 7, hpx: 6, shaded: true });
    flatParapetRoof(g, c, H, darken(0xb87a5e, 1.1));
    roofBox(g, c.sx / 2 - 10, c.sy / 2 - H + 4, 7, 0x9a6750);
    outlineBox(g, c, H);
    // Kiln stack.
    const stx = c.ex - 16;
    const sty = c.ey - H + 4;
    g.rect(stx - 4, sty - 30, 8, 30).fill(0x8a5a44);
    g.rect(stx - 4, sty - 30, 3, 30).fill(0x9a6a50);
    g.rect(stx - 5, sty - 32, 10, 3).fill(0x4a3626);
    // Finished panes leaning by the south corner.
    for (const off of [0, 4, 8] as const) {
      g.poly([c.sx - 14 + off, c.sy - 1, c.sx - 8 + off, c.sy - 12, c.sx - 6 + off, c.sy - 11, c.sx - 12 + off, c.sy]).fill({
        color: 0x9ac8d8,
        alpha: 0.9,
      });
    }
    signBoard(g, seWall(c, 0), 0.72, 14, 'GLASS');
    aoBase(g, swWall(c, H));
    aoBase(g, seWall(c, H));
    return { attachX: stx, attachY: sty - 32, anim: { kind: 'exhaust', x: stx, y: sty - 32 } };
  },
  chipFab: (g, c) => {
    const H = 34;
    shadowSkirt(g, c);
    boxWalls(g, c, H, 0xe8e4da);
    // Cleanroom window bands on both faces.
    for (const wl of [swWall(c, H), seWall(c, H)]) {
      const slope = wallSlope(wl);
      for (const t of [0.2, 0.5, 0.8]) {
        const p = wallPoint(wl, t, 20);
        g.poly(skewRect(g, p.x, p.y + 4, 16, 7, slope)).fill(0x36414d);
        g.poly(skewRect(g, p.x, p.y + 4, 16, 2, slope)).fill({ color: 0x8fc0dd, alpha: 0.6 });
      }
      // Base accent stripe.
      const b0 = wallPoint(wl, 0, 5);
      const b1 = wallPoint(wl, 1, 5);
      g.poly([b0.x, b0.y, b1.x, b1.y, b1.x, b1.y + 3, b0.x, b0.y + 3]).fill(0x5f87c9);
    }
    doorOn(g, seWall(c, 0), 0.5, 9, 13, 0x36414d);
    flatParapetRoof(g, c, H, 0xd8d4ca);
    roofBox(g, c.sx / 2 - 14, c.sy / 2 - H + 2, 9, 0xb8b4aa);
    roofBox(g, c.sx / 2 + 2, c.sy / 2 - H + 6, 7, 0xc4c0b6);
    roofBox(g, c.sx / 2 + 14, c.sy / 2 - H, 7, 0xb8b4aa);
    outlineBox(g, c, H);
    signBoard(g, seWall(c, 0), 0.78, H - 8, 'FAB');
    aoBase(g, swWall(c, H));
    aoBase(g, seWall(c, H));
    return { attachX: c.sx / 2, attachY: c.sy / 2 - H - 6, anim: null };
  },
  solarArray: (g, c) => {
    const cx = c.sx / 2;
    const cy = c.sy / 2;
    // Graded gravel pad.
    g.poly([0, 0, c.ex, c.ey, c.sx, c.sy, c.wx, c.wy]).fill(0xd0c4a4);
    g.poly([0, 0, c.ex, c.ey, c.sx, c.sy, c.wx, c.wy]).stroke({ width: 1, color: 0xb0a488 });
    // Three ranks of tilted panels along the NE axis, sun face up-left.
    for (const f of [0.28, 0.5, 0.72]) {
      const ax = c.wx * f;
      const ay = c.wy * f;
      const bx = c.ex + (c.sx - c.ex) * f;
      const by = c.ey + (c.sy - c.ey) * f;
      for (const t of [0.1, 0.3, 0.5, 0.7] as const) {
        const px = ax + (bx - ax) * t;
        const py = ay + (by - ay) * t;
        g.ellipse(px + 8, py + 3, 9, 2.5).fill({ color: 0x000000, alpha: 0.12 });
        g.rect(px - 1, py - 2, 2, 5).fill(0x8a8f98);
        g.rect(px + 13, py - 5, 2, 8).fill(0x8a8f98);
        g.poly([px - 3, py - 1, px + 17, py - 8, px + 19, py - 4, px - 1, py + 3]).fill(0x2e4668);
        g.poly([px - 3, py - 1, px + 17, py - 8, px + 17.7, py - 6.6, px - 2.3, py + 0.4]).fill({
          color: 0x8fc0dd,
          alpha: 0.55,
        });
        g.moveTo(px + 7, py - 3.5).lineTo(px + 8, py - 1).stroke({ width: 1, color: 0x1e3050, alpha: 0.8 });
      }
    }
    // Inverter cabinet by the south corner.
    g.rect(cx - 4, cy + 20, 9, 8).fill(0x99a0a8);
    g.rect(cx - 4, cy + 20, 9, 2.5).fill(0xa8adb5);
    g.rect(cx - 2, cy + 23, 2, 3).fill(0x3f444c);
    return { attachX: cx, attachY: cy + 16, anim: null };
  },
  nuclearPlant: (g, c) => {
    const H = 24;
    shadowSkirt(g, c);
    boxWalls(g, c, H, 0xc8c4bc);
    // Containment hall detail: tall shaded panels.
    for (const wl of [swWall(c, H), seWall(c, H)]) {
      siding(g, wl, 6, 0.08);
    }
    // Hazard band on the SE base (same language as the diesel generator).
    const se = seWall(c, H);
    const seLength = Math.hypot(se.x1 - se.x0, se.y1 - se.y0);
    const band0 = wallPoint(se, 0, 8);
    const band1 = wallPoint(se, 1, 8);
    g.poly([band0.x, band0.y, band1.x, band1.y, band1.x, band1.y + 5, band0.x, band0.y + 5]).fill(0x2a2a2e);
    for (let d = 3; d < seLength - 5; d += 7) {
      const p0 = wallPoint(se, d / seLength, 8);
      const p1 = wallPoint(se, (d + 3) / seLength, 8);
      g.poly([p0.x, p0.y, p1.x, p1.y, p1.x - 2, p1.y + 5, p0.x - 2, p0.y + 5]).fill(0xe8c170);
    }
    windowOn(g, se, 0.75, 16, { wpx: 7, hpx: 6, shaded: true });
    doorOn(g, se, 0.3, 8, 12, 0x3a3f46);
    flatParapetRoof(g, c, H, 0xd4d0c8);
    outlineBox(g, c, H);
    // Cooling tower rising behind the west corner: waisted silhouette.
    const tx = c.wx + 18;
    const ty = c.wy - H + 6;
    g.poly([tx - 15, ty, tx - 8, ty - 20, tx - 10, ty - 34, tx + 10, ty - 34, tx + 8, ty - 20, tx + 15, ty]).fill(0xd8d4cc);
    g.poly([tx - 15, ty, tx - 8, ty - 20, tx - 10, ty - 34, tx - 4, ty - 34, tx - 3, ty - 20, tx - 7, ty]).fill(0xe4e0d8);
    g.ellipse(tx, ty - 34, 10, 3).fill(0x8a8f98);
    g.ellipse(tx, ty, 15, 4).fill({ color: 0x000000, alpha: 0.15 });
    g.poly([tx - 15, ty, tx - 8, ty - 20, tx - 10, ty - 34, tx + 10, ty - 34, tx + 8, ty - 20, tx + 15, ty]).stroke({
      ...OUTLINE,
      alpha: 0.5,
    });
    // Trefoil placard.
    const px = wallPoint(se, 0.52, 12);
    g.circle(px.x, px.y, 4).fill(0xe8c170);
    g.circle(px.x, px.y, 1.2).fill(0x2a2a2e);
    for (const angle of [-Math.PI / 2, Math.PI / 6, (5 * Math.PI) / 6]) {
      g.poly([
        px.x + Math.cos(angle - 0.5) * 2, px.y + Math.sin(angle - 0.5) * 2,
        px.x + Math.cos(angle) * 4, px.y + Math.sin(angle) * 4,
        px.x + Math.cos(angle + 0.5) * 2, px.y + Math.sin(angle + 0.5) * 2,
      ]).fill(0x2a2a2e);
    }
    aoBase(g, swWall(c, H));
    aoBase(g, seWall(c, H));
    return { attachX: tx, attachY: ty - 36, anim: { kind: 'exhaust', x: tx, y: ty - 36 } };
  },
  gridInterconnect: (g, c) => {
    const cx = c.sx / 2;
    const cy = c.sy / 2;
    // Gravel switchyard pad with a fence line.
    g.poly([0, 0, c.ex, c.ey, c.sx, c.sy, c.wx, c.wy]).fill(0xc8bc9c);
    g.poly([0, 2, c.ex - 4, c.ey, c.sx, c.sy - 2, c.wx + 4, c.wy]).stroke({ width: 1, color: 0x8a8f98, alpha: 0.8 });
    // Lattice pylon: two legs, crossed bracing, twin crossarms.
    const baseY = cy + 6;
    const topY = cy - 44;
    g.ellipse(cx, baseY + 2, 12, 4).fill({ color: 0x000000, alpha: 0.18 });
    for (const side of [-1, 1] as const) {
      g.moveTo(cx + side * 9, baseY).lineTo(cx + side * 3, topY).stroke({ width: 2, color: 0x7d828c });
    }
    for (let level = 0; level < 4; level += 1) {
      const y0 = baseY - level * 12;
      const y1 = y0 - 12;
      const w0 = 9 - level * 1.5;
      const w1 = 9 - (level + 1) * 1.5;
      g.moveTo(cx - w0, y0).lineTo(cx + w1, y1).stroke({ width: 1, color: 0x8a8f98 });
      g.moveTo(cx + w0, y0).lineTo(cx - w1, y1).stroke({ width: 1, color: 0x8a8f98 });
      g.moveTo(cx - w0, y0).lineTo(cx + w0, y0).stroke({ width: 1, color: 0x7d828c });
    }
    for (const [armY, armW] of [[topY + 6, 16], [topY + 14, 13]] as const) {
      g.rect(cx - armW, armY, armW * 2, 2).fill(0x6a6f78);
      for (const side of [-1, 1] as const) {
        g.rect(cx + side * (armW - 2) - 1, armY + 2, 2, 3).fill(0x3f6f8f);
      }
    }
    g.rect(cx - 1, topY - 4, 2, 4).fill(0x6a6f78);
    // Control hut by the east corner.
    g.rect(c.ex / 2 + 4, c.ey / 2 + 8, 12, 9).fill(0x99a0a8);
    g.rect(c.ex / 2 + 4, c.ey / 2 + 8, 12, 3).fill(0xa8adb5);
    g.rect(c.ex / 2 + 8, c.ey / 2 + 12, 3, 5).fill(0x3f444c);
    return { attachX: cx, attachY: topY - 2, anim: null };
  },
  passengerTerminal: (g, c) => {
    const cx = c.sx / 2;
    const cy = c.sy / 2;
    // Board platform.
    g.poly([0, 0, c.ex, c.ey, c.sx, c.sy, c.wx, c.wy]).fill(0xb0895c);
    for (const f of [0.25, 0.5, 0.75]) {
      g.moveTo(c.wx * f, c.wy * f)
        .lineTo(c.ex + (c.sx - c.ex) * f, c.ey + (c.sy - c.ey) * f)
        .stroke({ width: 1, color: 0x8a6a3a, alpha: 0.6 });
    }
    // Canopy on posts over the waiting side.
    const H = 16;
    for (const [px, py] of [
      [c.wx + 6, c.wy + 2],
      [c.sx - 4, c.sy - 4],
      [c.ex - 8, c.ey + 2],
      [6, 2],
    ] as const) {
      g.rect(px - 1.5, py - H, 3, H).fill(0x6b4f36);
    }
    gableRoof(g, c, H, 7, 0x7a8a5e);
    // Ticket window kiosk under the canopy.
    g.rect(cx - 12, cy - 8, 12, 10).fill(0xe4d5b0);
    g.rect(cx - 12, cy - 8, 12, 3).fill(0xd8c9a4);
    g.rect(cx - 8, cy - 4, 5, 4).fill(0x36414d);
    // Bench and a luggage cart.
    g.rect(cx + 4, cy + 4, 10, 2).fill(0x8a6a3a);
    g.rect(cx + 4, cy + 6, 1.5, 3).fill(0x4a3626);
    g.rect(cx + 12.5, cy + 6, 1.5, 3).fill(0x4a3626);
    crate(g, cx + 20, cy + 10, 6);
    crate(g, cx + 25, cy + 12, 5);
    signBoard(g, seWall(c, 0), 0.5, H + 12, 'TICKETS');
    return { attachX: cx, attachY: cy - H - 12, anim: null };
  },
};

export function drawBuilding(g: Graphics, type: BuildingType, w: number, h: number): BuildingMeta {
  return DRAWERS[type](g, corners(w, h));
}
